// src/pages/AskOS.js
// [REDESIGNED] Student-facing AskOS — Tab-based navigation
//
// Tabs: Chat | Practice | Crossword | Progress | Mindmap
// Color scheme: Navy #1e3a6e · Blue #2563eb · Red #dc2626 · White · Near-black
//
// All original features preserved:
//   ✓ TTS, mic, language support, KaTeX math, image attachment
//   ✓ Mastery tracking, session persistence, credit quota
//   ✓ Settings panel (profile, language, voice, theme, accessibility)
//   ✓ Keyboard shortcuts, FloatingReader, Telegram integration
//
// NEW:
//   ✓ Tab navigation with deep-navy navbar
//   ✓ Profile photo (localStorage)  +  welcome-back card
//   ✓ Unsplash concept images (via /askos/unsplash endpoint)
//   ✓ Practice: AI-generated MCQ quiz + flip flashcards
//   ✓ Crossword: auto-generated word puzzle from syllabus concepts
//   ✓ Progress dashboard: mastery stats + SVG bar chart
//   ✓ Mindmap: SVG bezier tree with mastery colour-coding

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Row, Col, Form, Button, Alert,
    Badge, Spinner, Modal,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath  from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {
    GraduationCap, BookOpen, Send, Mic, MicOff,
    MessageSquare, LogOut, ChevronRight, Lightbulb, ChevronDown,
    RefreshCw, Volume2, VolumeX, BookMarked, Settings,
    User, Globe, Palette, Accessibility, Save, KeyRound,
    CheckCircle, Copy, Check, HelpCircle, Paperclip, X as XIcon,
    FlaskConical, Keyboard, Image as ImageIcon, Zap, AlertTriangle,
    ExternalLink, Unlink, BarChart2, GitBranch, RotateCcw,
    ChevronLeft, Award, Mail, ArrowLeft,
} from 'lucide-react';
import api from '../api/api';
import { logout, setBranding as setReduxBranding } from '../redux/slices/userSlice';
import '../components/Projects/Copilot.css';
import './AskOS.css';
import { showToast } from '../utils/toast';

// ─────────────────────────────────────────────────────────────────────────────
//  DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const NAVY  = '#1e3a6e';
const BLUE  = '#2563eb';
const RED   = '#dc2626';
const WHITE = '#ffffff';

// ─────────────────────────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const LANG_MAP = {
    en: 'en-IN', ta: 'ta-IN', hi: 'hi-IN', te: 'te-IN',
    ml: 'ml-IN', kn: 'kn-IN', bn: 'bn-IN', mr: 'mr-IN',
};
const LANGUAGES = [
    { code: 'en', label: 'English',   native: 'English'  },
    { code: 'ta', label: 'Tamil',     native: 'தமிழ்'     },
    { code: 'hi', label: 'Hindi',     native: 'हिन्दी'    },
    { code: 'te', label: 'Telugu',    native: 'తెలుగు'   },
    { code: 'ml', label: 'Malayalam', native: 'മലയാളം'   },
    { code: 'kn', label: 'Kannada',   native: 'ಕನ್ನಡ'    },
    { code: 'bn', label: 'Bengali',   native: 'বাংলা'    },
    { code: 'mr', label: 'Marathi',   native: 'मराठी'    },
];
const THEMES = [
    { key: 'light',         label: 'Light',        icon: '☀️', desc: 'Default white background'        },
    { key: 'dark',          label: 'Dark',          icon: '🌙', desc: 'Dark background, less eye strain' },
    { key: 'high-contrast', label: 'High Contrast', icon: '⬛', desc: 'Maximum contrast (WCAG AA)'      },
    { key: 'sepia',         label: 'Soft Sepia',    icon: '📜', desc: 'Warm tones, easier on the eyes'  },
];
const ACCENT_PRESETS = [
    { hex: '#6366f1', label: 'Indigo'  },
    { hex: '#2563eb', label: 'Blue'    },
    { hex: '#10b981', label: 'Emerald' },
    { hex: '#f59e0b', label: 'Amber'   },
    { hex: '#ef4444', label: 'Red'     },
    { hex: '#8b5cf6', label: 'Violet'  },
];
const FONT_SIZES    = [
    { key: 'sm', label: 'Small',   px: '14px' },
    { key: 'md', label: 'Default', px: '16px' },
    { key: 'lg', label: 'Large',   px: '18px' },
    { key: 'xl', label: 'X-Large', px: '20px' },
];
const LINE_SPACINGS = [
    { key: 'normal',  label: 'Normal',  lh: 1.5  },
    { key: 'relaxed', label: 'Relaxed', lh: 1.75 },
    { key: 'loose',   label: 'Loose',   lh: 2.1  },
];
const VOICE_SPEEDS  = [
    { value: 0.75, label: 'Slow'   },
    { value: 1.0,  label: 'Normal' },
    { value: 1.25, label: 'Fast'   },
];
const DEFAULT_PREFS = {
    theme: 'light', accent: '#6366f1', fontSize: 'md', lineSpacing: 'normal',
    reduceMotion: false, dyslexiaFont: false, focusIndicators: false,
    voiceEnabled: false, voiceSpeed: 1.0, voiceType: 'female', voiceName: null,
    language: 'en',
};
const TELEGRAM_BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || '';
const TELEGRAM_DEEP_LINK    = TELEGRAM_BOT_USERNAME ? `https://t.me/${TELEGRAM_BOT_USERNAME}` : '';

const TABS = [
    { id: 'chat',      label: 'Chat',      Icon: MessageSquare },
    { id: 'practice',  label: 'Practice',  Icon: FlaskConical  },
    { id: 'crossword', label: 'Crossword', Icon: Award         },
    { id: 'dashboard', label: 'Progress',  Icon: BarChart2     },
    { id: 'mindmap',   label: 'Mindmap',   Icon: GitBranch     },
];

// ─────────────────────────────────────────────────────────────────────────────
//  CROSSWORD ALGORITHM  (pure functions, no React)
// ─────────────────────────────────────────────────────────────────────────────
const CW_SIZE = 20;

function cwCanPlace(grid, word, row, col, dir) {
    const dr = dir === 'V' ? 1 : 0;
    const dc = dir === 'H' ? 1 : 0;
    const endR = row + dr * (word.length - 1);
    const endC = col + dc * (word.length - 1);
    if (row < 0 || col < 0 || endR >= CW_SIZE || endC >= CW_SIZE) return false;
    const bR = row - dr, bC = col - dc;
    if (bR >= 0 && bC >= 0 && grid[bR][bC]) return false;
    const aR = endR + dr, aC = endC + dc;
    if (aR < CW_SIZE && aC < CW_SIZE && grid[aR][aC]) return false;
    for (let i = 0; i < word.length; i++) {
        const r = row + dr * i;
        const c = col + dc * i;
        const cell = grid[r][c];
        if (cell && cell !== word[i]) return false;
        if (cell) continue;
        if (dir === 'H') {
            if (r > 0 && grid[r - 1][c]) return false;
            if (r < CW_SIZE - 1 && grid[r + 1][c]) return false;
        } else {
            if (c > 0 && grid[r][c - 1]) return false;
            if (c < CW_SIZE - 1 && grid[r][c + 1]) return false;
        }
    }
    return true;
}

function cwPlace(grid, word, row, col, dir) {
    const dr = dir === 'V' ? 1 : 0;
    const dc = dir === 'H' ? 1 : 0;
    for (let i = 0; i < word.length; i++) grid[row + dr * i][col + dc * i] = word[i];
}

function buildCrossword(wordList) {
    const grid = Array.from({ length: CW_SIZE }, () => Array(CW_SIZE).fill(''));
    const placements = [];
    const sorted = [...wordList].sort((a, b) => b.word.length - a.word.length);
    if (!sorted.length) return { grid: [], placements: [] };

    const first = sorted[0];
    const r0 = Math.floor(CW_SIZE / 2);
    const c0 = Math.floor((CW_SIZE - first.word.length) / 2);
    cwPlace(grid, first.word, r0, c0, 'H');
    placements.push({ ...first, row: r0, col: c0, dir: 'H', num: 1 });

    let num = 2;
    for (let wi = 1; wi < sorted.length; wi++) {
        const wc = sorted[wi];
        let placed = false;
        outer:
        for (const p of placements) {
            const newDir = p.dir === 'H' ? 'V' : 'H';
            for (let pi = 0; pi < p.word.length && !placed; pi++) {
                for (let si = 0; si < wc.word.length && !placed; si++) {
                    if (p.word[pi] !== wc.word[si]) continue;
                    const nr = p.dir === 'H' ? p.row - si : p.row + pi;
                    const nc = p.dir === 'H' ? p.col + pi : p.col - si;
                    if (cwCanPlace(grid, wc.word, nr, nc, newDir)) {
                        cwPlace(grid, wc.word, nr, nc, newDir);
                        placements.push({ ...wc, row: nr, col: nc, dir: newDir, num });
                        num++; placed = true; break outer;
                    }
                }
            }
        }
    }

    let minR = CW_SIZE, maxR = -1, minC = CW_SIZE, maxC = -1;
    grid.forEach((row, r) => row.forEach((cell, c) => {
        if (cell) { minR = Math.min(minR, r); maxR = Math.max(maxR, r); minC = Math.min(minC, c); maxC = Math.max(maxC, c); }
    }));
    if (maxR < 0) return { grid: [], placements: [] };
    const pad = 1;
    const r1 = Math.max(0, minR - pad), r2 = Math.min(CW_SIZE - 1, maxR + pad);
    const c1 = Math.max(0, minC - pad), c2 = Math.min(CW_SIZE - 1, maxC + pad);
    return {
        grid: grid.slice(r1, r2 + 1).map(row => row.slice(c1, c2 + 1)),
        placements: placements.map(p => ({ ...p, row: p.row - r1, col: p.col - c1 })),
    };
}

// ─────────────────────────────────────────────────────────────────────────────
//  PARSE AI JSON  (robust extraction from chat response)
// ─────────────────────────────────────────────────────────────────────────────
function parseAIJson(text, fallback = []) {
    if (!text) return fallback;
    try { return JSON.parse(text); } catch {}
    const m = text.match(/\[[\s\S]*\]/);
    if (m) { try { return JSON.parse(m[0]); } catch {} }
    return fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
//  PREFERENCE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const loadPrefs = () => {
    try {
        const raw = localStorage.getItem('askos_prefs');
        return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : { ...DEFAULT_PREFS };
    } catch { return { ...DEFAULT_PREFS }; }
};
const savePrefs = (prefs) => { localStorage.setItem('askos_prefs', JSON.stringify(prefs)); };
const applyPreferences = (prefs) => {
    const root = document.documentElement;
    root.setAttribute('data-askos-theme', prefs.theme);
    root.style.setProperty('--askos-accent', prefs.accent);
    root.style.setProperty('--askos-accent-light', prefs.accent + '22');
    root.style.setProperty('--askos-base-size', FONT_SIZES.find(f => f.key === prefs.fontSize)?.px || '16px');
    root.style.setProperty('--askos-line-height', LINE_SPACINGS.find(l => l.key === prefs.lineSpacing)?.lh || 1.5);
    if (prefs.dyslexiaFont) {
        root.style.setProperty('--askos-font-family', "'OpenDyslexic', 'Comic Sans MS', cursive");
        if (!document.getElementById('opendyslexic-css')) {
            const link = document.createElement('link');
            link.id = 'opendyslexic-css'; link.rel = 'stylesheet';
            link.href = 'https://fonts.cdnfonts.com/css/opendyslexic';
            document.head.appendChild(link);
        }
    } else {
        root.style.removeProperty('--askos-font-family');
    }
    document.body.classList.toggle('askos-reduce-motion', prefs.reduceMotion);
    document.body.classList.toggle('askos-dyslexia',       prefs.dyslexiaFont);
    document.body.classList.toggle('askos-focus-visible',  prefs.focusIndicators);
    root.setAttribute('data-askos-focus', prefs.focusIndicators ? 'visible' : 'default');
};

// ─────────────────────────────────────────────────────────────────────────────
//  SESSION / MASTERY HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const sessionStorageKey      = (id) => `askos_chat_${id}`;
const persistMessages        = (sid, msgs) => { if (sid) { try { localStorage.setItem(sessionStorageKey(sid), JSON.stringify(msgs)); } catch {} } };
const restoreMessages        = (sid) => { if (!sid) return null; try { const r = localStorage.getItem(sessionStorageKey(sid)); return r ? JSON.parse(r) : null; } catch { return null; } };
const masteryKey             = (slug, pid) => `askos_mastery_${slug}_${pid}`;
const loadMastery            = (slug, pid) => { try { const r = localStorage.getItem(masteryKey(slug, pid)); return r ? JSON.parse(r) : {}; } catch { return {}; } };
const markVisited            = (slug, pid, cid) => { const m = loadMastery(slug, pid); m[cid] = (m[cid] || 0) + 1; localStorage.setItem(masteryKey(slug, pid), JSON.stringify(m)); return m; };
const photoKey               = (slug) => `askos_photo_${slug}`;

// ─────────────────────────────────────────────────────────────────────────────
//  TTS / TEXT HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const stopSpeaking    = () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); };
const askosAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('askos_token')}` });

const toPlainText = (text) => text
    .replace(/\$\$[\s\S]*?\$\$/g, '').replace(/\$[^$\n]+\$/g, '')
    .replace(/\\\[[\s\S]*?\\\]/g, '').replace(/\\\([\s\S]*?\\\)/g, '')
    .replace(/```[\s\S]*?```/g, '').replace(/`[^`]*`/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')
    .replace(/#{1,6}\s/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/<[^>]+>/g, '').replace(/[_~]/g, '').trim();

const cleanForTTS = (text) => {
    if (!text) return '';
    return text
        .replace(/\$\$[\s\S]*?\$\$/g, '').replace(/\$[^$\n]+\$/g, '')
        .replace(/\\\[[\s\S]*?\\\]/g, '').replace(/\\\([\s\S]*?\\\)/g, '')
        .replace(/<[^>]*data-type=["']math[^"']*["'][^>]*>[\s\S]*?<\/[^>]+>/gi, '')
        .replace(/\b[A-Za-zη_]{1,6}\s*[=+\-*/^]\s*[A-Za-z0-9η_()\-+/*.^,\s]{1,40}/g, '')
        .replace(/\b\d+[\s]*[*/^][\s]*\d+/g, '').replace(/\([\d\s+\-*/^.]+\)/g, '')
        .replace(/```[\s\S]*?```/g, '').replace(/`[^`]+`/g, '')
        .replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')
        .replace(/#{1,6}\s/g, '').replace(/_([^_]+)_/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/<[^>]+>/g, '')
        .replace(/[*#~`\\]/g, '').replace(/\s{2,}/g, ' ').trim();
};

const speakText = (text, langCode = 'en', speed = 1.0, voiceType = 'female', voiceName = null) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const clean = cleanForTTS(text);
    if (!clean) return;
    const targetLang = LANG_MAP[langCode] || 'en-IN';
    const safeRate   = Math.min(Math.max(Number(speed) || 1.0, 0.5), 2.0);

    const doSpeak = (allVoices) => {
        const abbrevSafe = clean.replace(/\b(e\.g|i\.e|fig|eq|approx|vs|no|dr|mr|mrs|prof|sr|jr)\./gi, m => m.replace('.', '##DOT##'));
        const sentences  = (abbrevSafe.match(/[^.!?\n]+[.!?\n]*/g) || [abbrevSafe])
            .map(s => s.replace(/##DOT##/g, '.').trim()).filter(s => s.length > 1);

        let voice = null;
        if (voiceName) voice = allVoices.find(v => v.name === voiceName) || null;
        if (!voice) {
            const langBase   = targetLang.split('-')[0];
            const INDO_ARYAN = new Set(['hi', 'bn', 'mr', 'gu', 'pa', 'ur']);
            let pool = allVoices.filter(v => v.lang === targetLang || v.lang.replace('_','-') === targetLang || v.lang.replace('_','-').startsWith(langBase + '-'));
            if (pool.length === 0 && INDO_ARYAN.has(langBase))
                pool = allVoices.filter(v => v.lang === 'hi-IN' || v.lang.replace('_','-') === 'hi-IN' || v.name.toLowerCase().includes('hindi'));
            if (pool.length === 0)
                pool = allVoices.filter(v => { const l = v.lang.replace('_','-'); const n = v.name.toLowerCase(); return l === 'en-IN' || n.includes('india') || n.includes('heera') || n.includes('ravi') || n.includes('priya'); });
            if (pool.length === 0) pool = allVoices.filter(v => v.lang.startsWith('en'));
            if (pool.length === 0) pool = allVoices;
            const isMale   = (v) => /\b(male|ravi|prabhat|rishi|david|james|guy|mark|daniel)\b/i.test(v.name);
            const isFemale = (v) => /\b(female|heera|neerja|veena|lekha|aditi|zira|priya|tara|ananya|divya)\b/i.test(v.name);
            const isIndian = (v) => { const l = v.lang.replace('_','-'); const n = v.name.toLowerCase(); return l.endsWith('-IN') || n.includes('india') || n.includes('hindi') || isMale(v) || isFemale(v); };
            const googleIndian = pool.filter(v => v.name.toLowerCase().includes('google') && isIndian(v));
            const indian  = pool.filter(v => isIndian(v));
            const google  = pool.filter(v => v.name.toLowerCase().includes('google'));
            const ranked  = googleIndian.length ? googleIndian : indian.length ? indian : google.length ? google : pool;
            voice = voiceType === 'male'
                ? (ranked.find(v => isMale(v)) || pool.find(v => isMale(v)) || ranked[0] || pool[0])
                : (ranked.find(v => isFemale(v)) || pool.find(v => isFemale(v)) || ranked[0] || pool[0]);
        }
        sentences.forEach(s => {
            const u = new SpeechSynthesisUtterance(s);
            u.lang = voice?.lang || targetLang; u.rate = safeRate; u.pitch = 1.0;
            if (voice) u.voice = voice;
            window.speechSynthesis.speak(u);
        });
    };

    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) { doSpeak(voices); }
    else {
        const onReady = () => { window.speechSynthesis.removeEventListener('voiceschanged', onReady); doSpeak(window.speechSynthesis.getVoices()); };
        window.speechSynthesis.addEventListener('voiceschanged', onReady);
    }
};

const normalizeMathForMarkdown = (text = '') => {
    if (!text) return '';
    const dec = (f = '') => { try { return decodeURIComponent(f); } catch { return f; } };
    return text
        .replace(/<div\s+[^>]*data-type=["']math-block["'][^>]*data-formula=["']([^"']+)["'][^>]*>\s*<\/div>/gi, (_, f) => `\n\n$$\n${dec(f)}\n$$\n\n`)
        .replace(/<span\s+[^>]*data-type=["']math-inline["'][^>]*data-formula=["']([^"']+)["'][^>]*>\s*<\/span>/gi, (_, f) => `$${dec(f)}$`);
};

const extractFollowUps = (text) => {
    const match = text?.match(/\*\*Want to explore further\?\*\*([\s\S]*?)(?:$)/);
    if (!match) return [];
    return match[1].split('\n').map(l => l.replace(/^\d+\.\s*/, '').trim()).filter(Boolean).slice(0, 3);
};
const stripFollowUps = (text) => text?.replace(/\*\*Want to explore further\?\*\*[\s\S]*$/, '').trim() || '';

// ─────────────────────────────────────────────────────────────────────────────
//  LOGIN PAGE  (mode: 'login' | 'forgot' | 'sent' | 'reset' | 'reset-done')
// ─────────────────────────────────────────────────────────────────────────────
const AskOSLoginPage = ({ onLogin }) => {
    const { slug: urlSlug }              = useParams();
    const [searchParams]                 = useSearchParams();
    const [slug, setSlug]                = useState(urlSlug || searchParams.get('slug') || '');
    const [email, setEmail]              = useState('');
    const [password, setPassword]        = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading]          = useState(false);
    const [error, setError]              = useState('');
    const [mode, setMode]                = useState('login');
    const [forgotEmail, setForgotEmail]  = useState('');
    const [resetToken, setResetToken]    = useState('');
    const [newPassword, setNewPassword]  = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [localBranding, setLocalBranding] = useState({ institutionName: '', logoUrl: '' });
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Detect reset token in URL → switch to reset mode
    useEffect(() => {
        const token    = searchParams.get('reset_token');
        const slugParam= searchParams.get('slug');
        if (token) {
            setResetToken(token);
            if (slugParam) setSlug(slugParam);
            setMode('reset');
        }
    }, [searchParams]);

    // Fetch tenant branding when slug changes
    useEffect(() => {
        if (slug.length >= 3) {
            api.get(`/public/tenant-branding/${slug}`)
               .then(res => {
                   const b = { name: res.data.name || '', logo: res.data.logo || '' };
                   setLocalBranding({ institutionName: b.name, logoUrl: b.logo });
                   dispatch(setReduxBranding(b));
               })
               .catch(() => setLocalBranding({ institutionName: '', logoUrl: '' }));
        } else {
            setLocalBranding({ institutionName: '', logoUrl: '' });
        }
    }, [slug, dispatch]);

    // ── Login ────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!acceptedTerms) {
            setError('You must accept the Terms of Service and Privacy Policy to continue.');
            return;
        }
        setLoading(true); setError('');
        try {
            const res = await api.post(
                `/tenant/${slug}/askos/token?terms_accepted=true`,
                new URLSearchParams({ username: email, password })
            );
            const { access_token } = res.data;
            localStorage.setItem('askos_token', access_token);
            localStorage.setItem('askos_slug',  slug);
            onLogin({ slug, email, access_token });
        } catch (err) {
            setError(err.response?.data?.detail || 'Verification failed. Check your slug and credentials.');
        } finally { setLoading(false); }
    };

    // ── Forgot password ──────────────────────────────────────────────────────
    const handleForgot = async (e) => {
        e.preventDefault();
        if (!slug) { setError('Enter your University Slug first.'); return; }
        setLoading(true); setError('');
        try {
            await api.post(`/tenant/${slug}/askos/forgot-password`, { email: forgotEmail });
            setMode('sent');
        } catch (err) {
            setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally { setLoading(false); }
    };

    // ── Reset password ───────────────────────────────────────────────────────
    const handleReset = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
        if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
        setLoading(true); setError('');
        try {
            await api.post(`/tenant/${slug}/askos/reset-password`, { token: resetToken, new_password: newPassword });
            setMode('reset-done');
        } catch (err) {
            setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
        } finally { setLoading(false); }
    };

    const inputStyle = { border: 'none', borderBottom: '2px solid #e2e8f0', borderRadius: 0, padding: '8px 0', boxShadow: 'none', background: 'transparent' };
    const labelStyle = { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#64748b' };

    return (
        <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY} 45%, ${BLUE} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ width: '100%', maxWidth: 960, background: WHITE, borderRadius: 20, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', display: 'flex', minHeight: 580 }}>

                {/* Left panel — shared across all modes */}
                <div style={{ flex: 1, background: `linear-gradient(160deg, ${NAVY}, ${BLUE})`, padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: WHITE }}>
                    <div>
                        {localBranding.logoUrl
                            ? <img src={localBranding.logoUrl} alt={localBranding.institutionName} height="60" style={{ objectFit: 'contain', marginBottom: 24 }} onError={e => { e.target.style.display = 'none'; }} />
                            : <img src="/assets/images/logo.png" alt="AskOS" height="48" style={{ objectFit: 'contain', marginBottom: 24 }} onError={e => { e.target.style.display = 'none'; }} />}
                        <h2 style={{ fontWeight: 800, fontSize: '1.8rem', marginBottom: 8 }}>{localBranding.institutionName || 'AskOS'}</h2>
                        <p style={{ opacity: 0.75, fontSize: '1rem' }}>Your AI Professor — always available.</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        {[
                            { icon: '💬', title: 'Instant Doubt Clearance', desc: 'Ask anything — definitions, examples, or solved problems.' },
                            { icon: '🎯', title: 'Practice & Quiz',         desc: 'MCQ tests and flashcards generated from your syllabus.' },
                            { icon: '🗺️', title: 'Learning Mindmap',        desc: 'See your full course as an interactive knowledge tree.' },
                        ].map(f => (
                            <div key={f.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                                <span style={{ fontSize: '1.4rem', flexShrink: 0, marginTop: 2 }}>{f.icon}</span>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{f.title}</div>
                                    <div style={{ opacity: 0.7, fontSize: '0.8rem', marginTop: 2 }}>{f.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right panel — mode-conditional content */}
                <div style={{ width: 400, padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflowY: 'auto' }}>

                    {/* ── LOGIN MODE ──────────────────────────────────────── */}
                    {mode === 'login' && (
                        <>
                            <h3 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Student Sign In</h3>
                            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 28 }}>Access your AI professor workspace</p>

                            {error && <Alert variant="danger" className="py-2 small" role="alert">{error}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                {[
                                    { label: 'University Slug',  type: 'text',     val: slug,     set: v => setSlug(v.toLowerCase().trim()), ph: 'e.g. anna-univ'   },
                                    { label: 'University Email', type: 'email',    val: email,    set: setEmail,    ph: 'student@uni.edu'  },
                                    { label: 'Password',         type: 'password', val: password, set: setPassword, ph: '••••••••'          },
                                ].map(f => (
                                    <Form.Group className="mb-4" key={f.label}>
                                        <Form.Label style={labelStyle}>{f.label}</Form.Label>
                                        <Form.Control type={f.type} placeholder={f.ph} value={f.val} onChange={e => f.set(e.target.value)} required style={inputStyle} />
                                    </Form.Group>
                                ))}

                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, marginTop: -8 }}>
                                    <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: '#64748b', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'none' }}
                                        onClick={() => { setError(''); setForgotEmail(email); setMode('forgot'); }}>
                                        Forgot password?
                                    </button>
                                </div>

                                <Form.Group className="mb-4">
                                    <Form.Check type="checkbox" id="askos-terms-checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
                                        label={
                                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                I agree to the{' '}
                                                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontWeight: 600, textDecoration: 'none' }}>Terms of Service</a>
                                                {' '}and{' '}
                                                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontWeight: 600, textDecoration: 'none' }}>Privacy Policy</a>.
                                            </span>
                                        }
                                    />
                                </Form.Group>

                                <Button type="submit" disabled={loading || !acceptedTerms}
                                    style={{ width: '100%', background: acceptedTerms ? NAVY : '#94a3b8', border: 'none', borderRadius: 10, padding: '13px', fontWeight: 700, fontSize: '1rem', marginBottom: 16, transition: 'background 0.2s' }}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Enter AskOS →'}
                                </Button>
                            </Form>

                            {TELEGRAM_DEEP_LINK && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0' }}>
                                        <hr style={{ flex: 1, borderColor: '#e2e8f0', margin: 0 }} />
                                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>or</span>
                                        <hr style={{ flex: 1, borderColor: '#e2e8f0', margin: 0 }} />
                                    </div>
                                    <a href={TELEGRAM_DEEP_LINK} target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#229ED9', color: WHITE, borderRadius: 10, padding: '12px', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none', marginTop: 8 }}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/></svg>
                                        Continue with Telegram
                                    </a>
                                </>
                            )}

                            <div style={{ marginTop: 24, textAlign: 'center' }}>
                                <span style={{ background: '#f0f6ff', borderRadius: 20, padding: '6px 16px', fontSize: '0.78rem', color: BLUE, fontWeight: 600 }}>
                                    🎓 {slug ? `Student of ${slug.toUpperCase()}` : 'Student Portal'}
                                </span>
                            </div>
                        </>
                    )}

                    {/* ── FORGOT PASSWORD MODE ────────────────────────────── */}
                    {mode === 'forgot' && (
                        <>
                            <button type="button" style={{ background: 'none', border: 'none', padding: 0, marginBottom: 28, display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: '0.82rem', cursor: 'pointer' }}
                                onClick={() => { setError(''); setMode('login'); }}>
                                <ArrowLeft size={14} /> Back to sign in
                            </button>
                            <div style={{ marginBottom: 28 }}>
                                <div style={{ width: 48, height: 48, background: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                                    <Mail size={22} color={BLUE} />
                                </div>
                                <h4 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Forgot your password?</h4>
                                <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>Enter your email and we'll send a reset link valid for 10 minutes.</p>
                            </div>
                            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                            <Form onSubmit={handleForgot}>
                                {!slug && (
                                    <Form.Group className="mb-4">
                                        <Form.Label style={labelStyle}>University Slug</Form.Label>
                                        <Form.Control type="text" placeholder="e.g. anna-univ" value={slug} onChange={e => setSlug(e.target.value.toLowerCase().trim())} required style={inputStyle} />
                                    </Form.Group>
                                )}
                                <Form.Group className="mb-5">
                                    <Form.Label style={labelStyle}>University Email</Form.Label>
                                    <Form.Control type="email" placeholder="student@uni.edu" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required style={inputStyle} />
                                </Form.Group>
                                <Button type="submit" disabled={loading}
                                    style={{ width: '100%', background: NAVY, border: 'none', borderRadius: 10, padding: '13px', fontWeight: 700, fontSize: '1rem' }}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Send Reset Link'}
                                </Button>
                            </Form>
                        </>
                    )}

                    {/* ── SENT MODE ───────────────────────────────────────── */}
                    {mode === 'sent' && (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 56, marginBottom: 16 }}>📬</div>
                            <h4 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Check your inbox</h4>
                            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 28 }}>
                                If <strong>{forgotEmail}</strong> is registered, a password reset link has been sent. It expires in <strong>10 minutes</strong>.
                            </p>
                            <Button onClick={() => { setError(''); setMode('login'); }}
                                style={{ background: NAVY, border: 'none', borderRadius: 20, padding: '10px 28px', fontWeight: 700 }}>
                                Back to Sign In
                            </Button>
                            <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: 16 }}>
                                Didn't receive it? Check your spam folder or{' '}
                                <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: BLUE, fontSize: '0.75rem', cursor: 'pointer' }} onClick={() => setMode('forgot')}>try again</button>.
                            </p>
                        </div>
                    )}

                    {/* ── RESET PASSWORD MODE ─────────────────────────────── */}
                    {mode === 'reset' && (
                        <>
                            <div style={{ marginBottom: 28 }}>
                                <div style={{ width: 48, height: 48, background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                                    <KeyRound size={22} color="#16a34a" />
                                </div>
                                <h4 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Set a new password</h4>
                                <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>Choose a strong password for your account.</p>
                            </div>
                            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                            <Form onSubmit={handleReset}>
                                <Form.Group className="mb-4">
                                    <Form.Label style={labelStyle}>New Password</Form.Label>
                                    <Form.Control type="password" placeholder="Minimum 6 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} required style={inputStyle} />
                                </Form.Group>
                                <Form.Group className="mb-5">
                                    <Form.Label style={labelStyle}>Confirm Password</Form.Label>
                                    <Form.Control type="password" placeholder="Re-enter password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required style={inputStyle} />
                                </Form.Group>
                                <Button type="submit" disabled={loading}
                                    style={{ width: '100%', background: '#16a34a', border: 'none', borderRadius: 10, padding: '13px', fontWeight: 700, fontSize: '1rem' }}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Reset Password'}
                                </Button>
                            </Form>
                        </>
                    )}

                    {/* ── RESET DONE MODE ─────────────────────────────────── */}
                    {mode === 'reset-done' && (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
                            <h4 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Password reset!</h4>
                            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 28 }}>
                                Your password has been updated. You can now sign in with your new password.
                            </p>
                            <Button onClick={() => { setError(''); setMode('login'); }}
                                style={{ background: NAVY, border: 'none', borderRadius: 20, padding: '10px 28px', fontWeight: 700 }}>
                                Go to Sign In
                            </Button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  SETTINGS PANEL — shared sub-components
// ─────────────────────────────────────────────────────────────────────────────
const OptionCard = ({ selected, onClick, children }) => (
    <button onClick={onClick}
        className="border rounded-3 p-3 text-start w-100 mb-2 position-relative"
        style={{ background: selected ? 'var(--askos-accent-light, #6366f122)' : 'transparent', outline: selected ? '2px solid var(--askos-accent, #6366f1)' : 'none', outlineOffset: '1px', cursor: 'pointer', transition: 'all 0.15s' }}
        aria-pressed={selected}>
        {selected && <CheckCircle size={14} className="position-absolute top-0 end-0 m-2" style={{ color: 'var(--askos-accent, #6366f1)' }} />}
        {children}
    </button>
);

const ToggleRow = ({ id, label, description, checked, onChange }) => (
    <div className="d-flex align-items-start justify-content-between gap-3 py-3 border-bottom">
        <div>
            <label htmlFor={id} className="fw-semibold small mb-0" style={{ cursor: 'pointer' }}>{label}</label>
            {description && <p className="x-small text-muted mb-0 mt-1">{description}</p>}
        </div>
        <Form.Check type="switch" id={id} checked={checked} onChange={e => onChange(e.target.checked)} aria-label={label} />
    </div>
);

const SectionLabel = ({ children }) => (
    <p className="x-small fw-bold text-uppercase mb-3 mt-1" style={{ color: 'var(--askos-accent, #6366f1)', letterSpacing: '0.07em' }}>{children}</p>
);

const PillGroup = ({ options, value, onChange, getLabel, getValue }) => (
    <div className="d-flex gap-2 flex-wrap">
        {options.map(opt => {
            const v = getValue(opt); const active = value === v;
            return (
                <button key={v} onClick={() => onChange(v)} className="rounded-pill border px-3 py-1 small fw-semibold"
                    style={{ background: active ? 'var(--askos-accent, #6366f1)' : 'transparent', color: active ? '#fff' : undefined, borderColor: active ? 'var(--askos-accent, #6366f1)' : undefined, cursor: 'pointer' }}
                    aria-pressed={active}>{getLabel(opt)}</button>
            );
        })}
    </div>
);

const UInput = ({ label, helpText, ...props }) => (
    <Form.Group className="mb-3">
        <Form.Label className="x-small fw-bold text-muted text-uppercase">{label}</Form.Label>
        <Form.Control className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" {...props} />
        {helpText && <Form.Text className="x-small text-muted">{helpText}</Form.Text>}
    </Form.Group>
);

const TelegramSVG = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
    </svg>
);

const ProfileTab = ({ profile, slug, onPhotoChange }) => {
    const [fullName,         setFullName]         = useState(profile?.full_name || '');
    const [phoneNum,         setPhoneNum]         = useState(profile?.phone_number || '');
    const [telegramId,       setTelegramId]       = useState(profile?.telegram_id || '');
    const [telegramUsername, setTelegramUsername] = useState(profile?.telegram_username || '');
    const [currentPw,  setCurrentPw]  = useState('');
    const [newPw,      setNewPw]      = useState('');
    const [confirmPw,  setConfirmPw]  = useState('');
    const [saving,     setSaving]     = useState(false);
    const [msg,        setMsg]        = useState(null);
    const [linkOtp,    setLinkOtp]    = useState(null);
    const [localPhoto, setLocalPhoto] = useState(() => localStorage.getItem(photoKey(slug)) || '');
    const isLinked = !!profile?.telegram_id;

    const handlePhotoUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { setMsg({ type: 'danger', text: 'Please select an image file.' }); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const data = ev.target.result;
            if (data.length > 400_000) { setMsg({ type: 'danger', text: 'Photo too large. Use an image under 300 KB.' }); return; }
            localStorage.setItem(photoKey(slug), data);
            setLocalPhoto(data);
            onPhotoChange?.(data);
            // Persist to backend so photo survives across devices and browser clears
            api.patch(`/tenant/${slug}/askos/me`, { profile_photo: data }, { headers: askosAuthHeader() }).catch(() => {});
            setMsg({ type: 'success', text: 'Profile photo updated!' });
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const clearPhoto = () => {
        localStorage.removeItem(photoKey(slug));
        setLocalPhoto('');
        onPhotoChange?.('');
        api.patch(`/tenant/${slug}/askos/me`, { profile_photo: null }, { headers: askosAuthHeader() }).catch(() => {});
    };

    const saveProfile = async () => {
        setSaving(true); setMsg(null);
        try {
            await api.patch(`/tenant/${slug}/askos/me`, { full_name: fullName, phone_number: phoneNum || null, telegram_id: telegramId || null, telegram_username: telegramUsername || null }, { headers: askosAuthHeader() });
            setMsg({ type: 'success', text: 'Profile updated.' });
        } catch (e) { setMsg({ type: 'danger', text: e.response?.data?.detail || 'Update failed.' }); }
        finally { setSaving(false); }
    };

    const changePassword = async () => {
        if (newPw !== confirmPw) { setMsg({ type: 'danger', text: 'Passwords do not match.' }); return; }
        if (newPw.length < 6)   { setMsg({ type: 'danger', text: 'Password must be at least 6 characters.' }); return; }
        setSaving(true); setMsg(null);
        try {
            await api.patch(`/tenant/${slug}/askos/me`, { current_password: currentPw, new_password: newPw }, { headers: askosAuthHeader() });
            setCurrentPw(''); setNewPw(''); setConfirmPw('');
            setMsg({ type: 'success', text: 'Password changed.' });
        } catch (e) { setMsg({ type: 'danger', text: e.response?.data?.detail || 'Password change failed.' }); }
        finally { setSaving(false); }
    };

    const handleGenerateLinkCode = async () => {
        setSaving(true); setMsg(null);
        try {
            const res = await api.post(`/tenant/${slug}/askos/me/telegram/link-code`, {}, { headers: askosAuthHeader() });
            if (res.data.already_linked) setMsg({ type: 'info', text: 'Your Telegram account is already linked.' });
            else setLinkOtp(res.data);
        } catch (e) { setMsg({ type: 'danger', text: e.response?.data?.detail || 'Could not generate link code.' }); }
        finally { setSaving(false); }
    };

    const handleUnlinkTelegram = async () => {
        if (!window.confirm('Unlink your Telegram account from AskOS?')) return;
        setSaving(true); setMsg(null);
        try {
            await api.delete(`/tenant/${slug}/askos/me/telegram/unlink`, { headers: askosAuthHeader() });
            setTelegramId(''); setTelegramUsername('');
            setMsg({ type: 'success', text: 'Telegram account unlinked. Please refresh the page.' });
        } catch (e) { setMsg({ type: 'danger', text: e.response?.data?.detail || 'Unlink failed.' }); }
        finally { setSaving(false); }
    };

    return (
        <div>
            {msg && <Alert variant={msg.type} className="py-2 small mb-3" role="status">{msg.text}</Alert>}

            <SectionLabel>Profile Photo</SectionLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', border: '2px solid var(--askos-accent, #6366f1)', background: localPhoto ? 'transparent' : 'var(--askos-accent-light, #6366f122)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {localPhoto ? <img src={localPhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <User size={28} style={{ color: 'var(--askos-accent, #6366f1)' }} />}
                </div>
                <div>
                    <label style={{ display: 'inline-block', padding: '5px 14px', border: '1px solid #e2e8f0', borderRadius: 20, fontSize: '0.8rem', cursor: 'pointer', marginBottom: 4 }}>
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                        Upload Photo
                    </label>
                    {localPhoto && <button onClick={clearPhoto} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.75rem', cursor: 'pointer', display: 'block', padding: '2px 0' }}>Remove photo</button>}
                    <p className="x-small text-muted mb-0">Stored locally · Max 300 KB</p>
                </div>
            </div>

            <SectionLabel>Personal Info</SectionLabel>
            <UInput label="Full Name" value={fullName} onChange={e => setFullName(e.target.value)} />
            <UInput label="Email" value={profile?.email || ''} readOnly disabled />

            <SectionLabel>Messaging Integrations</SectionLabel>
            <UInput label="Telegram Chat ID" placeholder="e.g. 123456789" value={telegramId} onChange={e => setTelegramId(e.target.value)} helpText="Auto-populated when you connect below." />
            <UInput label="Telegram Username" placeholder="studenthandle (without @)" value={telegramUsername} onChange={e => setTelegramUsername(e.target.value)} />
            <UInput label="Phone Number (WhatsApp)" placeholder="+919876543210" value={phoneNum} onChange={e => setPhoneNum(e.target.value)} helpText="Include country code, no spaces (E.164)." />
            <Button size="sm" className="rounded-pill px-4 mb-5" onClick={saveProfile} disabled={saving}
                style={{ background: 'var(--askos-accent, #6366f1)', border: 'none' }}>
                {saving ? <Spinner animation="border" size="sm" /> : <><Save size={13} className="me-1" />Save</>}
            </Button>

            <SectionLabel>Telegram Auto-Connect</SectionLabel>
            {isLinked ? (
                <div className="p-3 rounded-3 mb-4" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <div className="d-flex align-items-start justify-content-between gap-3">
                        <div>
                            <p className="small fw-semibold mb-1" style={{ color: '#15803d' }}><TelegramSVG size={14} /> Telegram Linked</p>
                            <p className="x-small text-muted mb-0">ID: <code>{profile.telegram_id}</code>{profile.telegram_username && <span className="ms-2">· @{profile.telegram_username}</span>}</p>
                        </div>
                        <Button size="sm" variant="outline-danger" className="rounded-pill d-flex align-items-center gap-1 flex-shrink-0" onClick={handleUnlinkTelegram} disabled={saving}><Unlink size={12} />Unlink</Button>
                    </div>
                </div>
            ) : linkOtp ? (
                <div className="p-3 rounded-3 mb-4" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <p className="small fw-semibold mb-2" style={{ color: '#1d4ed8' }}>Send this code to the AskOS bot on Telegram</p>
                    <div className="d-flex align-items-center gap-3 mb-3">
                        <code className="px-3 py-2 rounded-2 fw-bold" style={{ background: '#dbeafe', color: '#1e40af', fontSize: '1.4rem', letterSpacing: '0.2em' }}>{linkOtp.otp}</code>
                        <span className="x-small text-muted">Expires in {linkOtp.expires_in}</span>
                    </div>
                    {linkOtp.deep_link && (
                        <a href={linkOtp.deep_link} target="_blank" rel="noopener noreferrer" className="btn btn-sm rounded-pill d-inline-flex align-items-center gap-2 fw-semibold text-white" style={{ background: '#229ED9', border: 'none', textDecoration: 'none' }}>
                            <TelegramSVG size={15} />Open Telegram Bot<ExternalLink size={12} />
                        </a>
                    )}
                </div>
            ) : (
                <div className="mb-4">
                    <p className="small text-muted mb-3">Generate a secure code to link your Telegram account automatically.</p>
                    <Button size="sm" className="rounded-pill d-flex align-items-center gap-2" onClick={handleGenerateLinkCode} disabled={saving} style={{ background: '#229ED9', border: 'none', color: '#fff' }}>
                        {saving ? <Spinner animation="border" size="sm" /> : <><TelegramSVG size={15} />Generate Connect Code</>}
                    </Button>
                </div>
            )}

            <SectionLabel>Change Password</SectionLabel>
            <UInput label="Current Password" type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="••••••••" />
            <UInput label="New Password"     type="password" value={newPw}     onChange={e => setNewPw(e.target.value)}     placeholder="Min 6 characters" />
            <UInput label="Confirm Password" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Repeat new password" />
            <Button size="sm" variant="outline-secondary" className="rounded-pill px-4" onClick={changePassword} disabled={saving || !currentPw || !newPw}>
                {saving ? <Spinner animation="border" size="sm" /> : <><KeyRound size={13} className="me-1" />Change Password</>}
            </Button>
        </div>
    );
};

const LanguageTab = ({ prefs, onChange }) => (
    <div>
        <SectionLabel>Preferred Language</SectionLabel>
        <p className="small text-muted mb-4">Controls mic language and TTS voice language.</p>
        <Row className="g-2">
            {LANGUAGES.map(lang => (
                <Col xs={6} key={lang.code}>
                    <OptionCard selected={prefs.language === lang.code} onClick={() => onChange({ ...prefs, language: lang.code })}>
                        <div className="d-flex align-items-center gap-2">
                            <span style={{ fontSize: '1.2rem' }}>🌐</span>
                            <div><div className="fw-semibold small">{lang.label}</div><div className="x-small text-muted">{lang.native}</div></div>
                        </div>
                    </OptionCard>
                </Col>
            ))}
        </Row>
    </div>
);

const VoiceModelSelector = ({ prefs, onChange }) => {
    const [voices, setVoices] = useState([]);
    useEffect(() => {
        const load = () => setVoices(window.speechSynthesis?.getVoices() || []);
        load();
        window.speechSynthesis?.addEventListener('voiceschanged', load);
        return () => window.speechSynthesis?.removeEventListener('voiceschanged', load);
    }, []);
    if (voices.length === 0) return null;
    const INDIAN_LANGS = new Set(['hi-IN','ta-IN','te-IN','ml-IN','kn-IN','bn-IN','mr-IN','en-IN']);
    const norm = v => v.lang.replace('_', '-');
    const indian  = voices.filter(v => INDIAN_LANGS.has(norm(v)));
    const english = voices.filter(v => v.lang.startsWith('en') && !INDIAN_LANGS.has(norm(v)));
    const other   = voices.filter(v => !v.lang.startsWith('en') && !INDIAN_LANGS.has(norm(v)));
    return (
        <div className="pt-3 mb-4">
            <p className="fw-semibold small mb-1">Voice Model</p>
            <Form.Select size="sm" value={prefs.voiceName || ''} onChange={e => onChange({ ...prefs, voiceName: e.target.value || null })}>
                <option value="">Auto (recommended)</option>
                {indian.length  > 0 && <optgroup label={`— Indian Voices (${indian.length}) —`}>{indian.map(v  => <option key={v.voiceURI} value={v.name}>{v.name} [{v.lang}]</option>)}</optgroup>}
                {english.length > 0 && <optgroup label={`— English Voices (${english.length}) —`}>{english.map(v => <option key={v.voiceURI} value={v.name}>{v.name} [{v.lang}]</option>)}</optgroup>}
                {other.length   > 0 && <optgroup label={`— Other (${other.length}) —`}>{other.map(v   => <option key={v.voiceURI} value={v.name}>{v.name} [{v.lang}]</option>)}</optgroup>}
            </Form.Select>
        </div>
    );
};

const VoiceTab = ({ prefs, onChange }) => (
    <div>
        <SectionLabel>Voice Responses</SectionLabel>
        <ToggleRow id="voice-toggle" label="Read responses aloud" description="AskOS will speak each AI response using your browser's built-in TTS." checked={prefs.voiceEnabled} onChange={v => onChange({ ...prefs, voiceEnabled: v })} />
        {prefs.voiceEnabled && (
            <>
                <VoiceModelSelector prefs={prefs} onChange={onChange} />
                {!prefs.voiceName && (
                    <div className="pt-3 mb-4">
                        <p className="fw-semibold small mb-2">Voice Type</p>
                        <PillGroup options={[{ v: 'female', l: 'Female' }, { v: 'male', l: 'Male' }]} value={prefs.voiceType || 'female'} getLabel={s => s.l} getValue={s => s.v} onChange={v => onChange({ ...prefs, voiceType: v })} />
                    </div>
                )}
                <div className="pt-3 mb-4">
                    <p className="fw-semibold small mb-2">Speed</p>
                    <PillGroup options={VOICE_SPEEDS} value={prefs.voiceSpeed} getLabel={s => s.label} getValue={s => s.value} onChange={v => onChange({ ...prefs, voiceSpeed: v })} />
                </div>
                <Button size="sm" variant="outline-secondary" className="rounded-pill"
                    onClick={() => speakText("Hello! I am AskOS, your AI professor. Voice settings are working correctly.", prefs.language, Number(prefs.voiceSpeed) || 1.0, prefs.voiceType || 'female', prefs.voiceName || null)}>
                    <Volume2 size={13} className="me-2" />Play test
                </Button>
            </>
        )}
    </div>
);

const ThemeTab = ({ prefs, onChange }) => (
    <div>
        <SectionLabel>Theme</SectionLabel>
        {THEMES.map(t => (
            <OptionCard key={t.key} selected={prefs.theme === t.key} onClick={() => onChange({ ...prefs, theme: t.key })}>
                <div className="d-flex align-items-center gap-3">
                    <span style={{ fontSize: '1.4rem' }}>{t.icon}</span>
                    <div><div className="fw-semibold small">{t.label}</div><div className="x-small text-muted">{t.desc}</div></div>
                </div>
            </OptionCard>
        ))}
        <SectionLabel>Accent Colour</SectionLabel>
        <div className="d-flex align-items-center gap-3 flex-wrap">
            {ACCENT_PRESETS.map(a => (
                <button key={a.hex} title={a.label} onClick={() => onChange({ ...prefs, accent: a.hex })} aria-label={`Accent: ${a.label}`} aria-pressed={prefs.accent === a.hex}
                    style={{ width: 34, height: 34, borderRadius: '50%', background: a.hex, border: 'none', cursor: 'pointer', outline: prefs.accent === a.hex ? `3px solid ${a.hex}` : '3px solid transparent', outlineOffset: 3, transform: prefs.accent === a.hex ? 'scale(1.18)' : 'scale(1)', transition: 'all 0.15s' }} />
            ))}
            <input type="color" value={prefs.accent} onChange={e => onChange({ ...prefs, accent: e.target.value })} title="Custom colour" style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 2 }} />
        </div>
    </div>
);

const AccessibilityTab = ({ prefs, onChange }) => (
    <div>
        <SectionLabel>Text &amp; Reading</SectionLabel>
        <div className="mb-4">
            <p className="fw-semibold small mb-2">Font size</p>
            <PillGroup options={FONT_SIZES} value={prefs.fontSize} getLabel={f => f.label} getValue={f => f.key} onChange={v => onChange({ ...prefs, fontSize: v })} />
        </div>
        <div className="mb-4">
            <p className="fw-semibold small mb-2">Line spacing</p>
            <PillGroup options={LINE_SPACINGS} value={prefs.lineSpacing} getLabel={l => l.label} getValue={l => l.key} onChange={v => onChange({ ...prefs, lineSpacing: v })} />
        </div>
        <SectionLabel>Vision &amp; Motion</SectionLabel>
        <ToggleRow id="dyslexia"      label="Dyslexia-friendly font"     description="Uses OpenDyslexic — designed to reduce letter confusion."                  checked={prefs.dyslexiaFont}     onChange={v => onChange({ ...prefs, dyslexiaFont: v })} />
        <ToggleRow id="reduce-motion" label="Reduce motion"              description="Disables animations and transitions. Meets WCAG 2.1 SC 2.3.3."            checked={prefs.reduceMotion}     onChange={v => onChange({ ...prefs, reduceMotion: v })} />
        <SectionLabel>Interaction</SectionLabel>
        <ToggleRow id="focus-rings"   label="Enhanced focus indicators"  description="Large, high-visibility keyboard focus rings."                             checked={prefs.focusIndicators}  onChange={v => onChange({ ...prefs, focusIndicators: v })} />
    </div>
);

const ShortcutsModal = ({ show, onHide }) => (
    <Modal show={show} onHide={onHide} centered size="sm">
        <Modal.Header closeButton className="border-0 pb-0"><Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2"><Keyboard size={16} className="text-primary" />Keyboard Shortcuts</Modal.Title></Modal.Header>
        <Modal.Body className="pt-2">
            {[
                { keys: 'Ctrl + M', desc: 'Toggle microphone'          },
                { keys: 'Ctrl + R', desc: 'Read last response aloud'   },
                { keys: 'Ctrl + B', desc: 'Bookmark last response'     },
                { keys: 'Ctrl + ,', desc: 'Open settings'              },
                { keys: 'Escape',   desc: 'Stop voice / clear image'   },
                { keys: 'Enter',    desc: 'Send message'               },
                { keys: 'Shift+Enter', desc: 'New line'                },
            ].map(({ keys, desc }) => (
                <div key={keys} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                    <span className="small text-muted">{desc}</span>
                    <kbd className="px-2 py-1 rounded small fw-bold" style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', fontSize: '0.72rem' }}>{keys}</kbd>
                </div>
            ))}
        </Modal.Body>
    </Modal>
);

const SETTINGS_TABS = [
    { key: 'profile',       label: 'Profile',        Icon: User          },
    { key: 'language',      label: 'Language',       Icon: Globe         },
    { key: 'voice',         label: 'Voice',          Icon: Volume2       },
    { key: 'theme',         label: 'Theme & Colour', Icon: Palette       },
    { key: 'accessibility', label: 'Accessibility',  Icon: Accessibility },
];

const SettingsPanel = ({ show, onHide, profile, slug, prefs, onPrefsChange, onPhotoChange }) => {
    const [tab,   setTab]   = useState('profile');
    const [saved, setSaved] = useState(false);
    const handleChange = (newPrefs) => { onPrefsChange(newPrefs); savePrefs(newPrefs); applyPreferences(newPrefs); };
    const handleSave   = () => { savePrefs(prefs); applyPreferences(prefs); setSaved(true); setTimeout(() => setSaved(false), 2000); };
    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton className="border-0 pb-0"><Modal.Title className="fw-bold fs-6">Student Settings</Modal.Title></Modal.Header>
            <Modal.Body className="p-0" style={{ minHeight: 480 }}>
                <div className="d-flex" style={{ minHeight: 480 }}>
                    <div className="border-end py-3 flex-shrink-0" style={{ width: 180 }}>
                        {SETTINGS_TABS.map(({ key, label, Icon }) => (
                            <button key={key} onClick={() => setTab(key)} className="d-flex align-items-center gap-2 w-100 border-0 px-3 py-2 text-start small"
                                style={{ background: tab === key ? 'var(--askos-accent-light, #6366f122)' : 'transparent', color: tab === key ? 'var(--askos-accent, #6366f1)' : undefined, fontWeight: tab === key ? 600 : undefined, borderRight: tab === key ? '2px solid var(--askos-accent, #6366f1)' : '2px solid transparent', cursor: 'pointer' }}>
                                <Icon size={15} />{label}
                            </button>
                        ))}
                    </div>
                    <div className="flex-grow-1 p-4 overflow-auto">
                        {tab === 'profile'       && <ProfileTab       profile={profile} slug={slug} onPhotoChange={onPhotoChange} />}
                        {tab === 'language'      && <LanguageTab      prefs={prefs} onChange={handleChange} />}
                        {tab === 'voice'         && <VoiceTab         prefs={prefs} onChange={handleChange} />}
                        {tab === 'theme'         && <ThemeTab         prefs={prefs} onChange={handleChange} />}
                        {tab === 'accessibility' && <AccessibilityTab prefs={prefs} onChange={handleChange} />}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 gap-2">
                {saved && <span className="small text-success d-flex align-items-center gap-1 me-auto"><CheckCircle size={14} />Preferences saved</span>}
                {tab !== 'profile' && (
                    <Button size="sm" className="rounded-pill px-4" onClick={handleSave} style={{ background: 'var(--askos-accent, #6366f1)', border: 'none' }}>
                        <Save size={13} className="me-1" />Save Preferences
                    </Button>
                )}
                <Button size="sm" variant="light" className="rounded-pill px-3" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  PRACTICE WORKSPACE — MCQ + Flashcards
// ─────────────────────────────────────────────────────────────────────────────
const PracticeWorkspace = ({ activeProject, activeConcept, slug }) => {
    const [mode,       setMode]       = useState('idle');
    const [questions,  setQuestions]  = useState([]);
    const [flashcards, setFlashcards] = useState([]);
    const [answers,    setAnswers]    = useState({});
    const [submitted,  setSubmitted]  = useState(false);
    const [flipped,    setFlipped]    = useState({});
    const [cardIdx,    setCardIdx]    = useState(0);
    const [err,        setErr]        = useState('');

    const callAI = async (prompt) => {
        if (!activeConcept || !activeProject) return null;
        const sessRes = await api.post(`/tenant/${slug}/askos/session/init`,
            { project_id: activeProject.id, concept_id: activeConcept.id },
            { headers: askosAuthHeader() });
        const res = await api.post(`/tenant/${slug}/askos/chat`,
            { session_id: sessRes.data.session_id, message: prompt, concept_id: activeConcept.id, language: 'en' },
            { headers: askosAuthHeader() });
        return res.data.history?.slice(-1)[0]?.content || '';
    };

    const generateMCQ = async () => {
        setMode('loading'); setErr('');
        try {
            const content = await callAI(
                `Generate exactly 5 multiple choice questions about "${activeConcept.name}". Return ONLY a valid JSON array with no other text:\n[{"q":"Question?","opts":["A) option","B) option","C) option","D) option"],"ans":"A","explanation":"Why A is correct"}]`
            );
            const parsed = parseAIJson(content, []);
            if (parsed.length) { setQuestions(parsed); setAnswers({}); setSubmitted(false); setMode('mcq'); }
            else { setErr('Could not parse MCQ response. Try again.'); setMode('idle'); }
        } catch { setErr('Generation failed. Please try again.'); setMode('idle'); }
    };

    const generateFlashcards = async () => {
        setMode('loading'); setErr('');
        try {
            const content = await callAI(
                `Generate exactly 6 flashcard pairs about "${activeConcept.name}". Return ONLY a valid JSON array:\n[{"front":"Key term or concept","back":"Definition or explanation"}]`
            );
            const parsed = parseAIJson(content, []);
            if (parsed.length) { setFlashcards(parsed); setFlipped({}); setCardIdx(0); setMode('flashcard'); }
            else { setErr('Could not parse flashcard response. Try again.'); setMode('idle'); }
        } catch { setErr('Generation failed. Please try again.'); setMode('idle'); }
    };

    const score = submitted ? questions.filter((q, i) => answers[i] === q.ans).length : 0;

    if (!activeConcept) return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
            <FlaskConical size={56} style={{ color: '#94a3b8', marginBottom: 16 }} />
            <h5 style={{ fontWeight: 700, color: '#334155', marginBottom: 8 }}>Ready to Practice?</h5>
            <p style={{ color: '#64748b', maxWidth: 320, lineHeight: 1.6 }}>Select a topic from the sidebar first, then generate MCQ questions or flip flashcards to test your knowledge.</p>
        </div>
    );

    if (mode === 'loading') return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <Spinner animation="border" style={{ color: BLUE, width: 48, height: 48, borderWidth: 4 }} />
            <p style={{ color: '#475569', fontWeight: 600 }}>Generating practice material for <em>{activeConcept.name}</em>…</p>
        </div>
    );

    if (mode === 'mcq') return (
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <button onClick={() => setMode('idle')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}>
                    <ChevronLeft size={16} /> Back
                </button>
                <h5 style={{ margin: 0, fontWeight: 700, color: NAVY }}>Quiz: {activeConcept.name}</h5>
                {submitted && (
                    <span style={{ marginLeft: 'auto', background: score >= questions.length * 0.8 ? '#dcfce7' : '#fef9c3', color: score >= questions.length * 0.8 ? '#15803d' : '#92400e', borderRadius: 20, padding: '4px 14px', fontWeight: 700, fontSize: '0.85rem' }}>
                        {score}/{questions.length} correct
                    </span>
                )}
            </div>
            {err && <Alert variant="danger" className="small py-2">{err}</Alert>}
            {questions.map((q, qi) => (
                <div key={qi} style={{ background: WHITE, borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: submitted && answers[qi] !== q.ans ? '1px solid #fca5a5' : submitted && answers[qi] === q.ans ? '1px solid #86efac' : '1px solid #e2e8f0' }}>
                    <p style={{ fontWeight: 700, color: '#0f172a', marginBottom: 14, fontSize: '0.95rem' }}>{qi + 1}. {q.q}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {(q.opts || []).map((opt, oi) => {
                            const letter     = opt.match(/^([A-D])[).]/)?.[1] || String.fromCharCode(65 + oi);
                            const isSelected = answers[qi] === letter;
                            const isCorrect  = submitted && letter === q.ans;
                            const isWrong    = submitted && isSelected && letter !== q.ans;
                            return (
                                <button key={oi} onClick={() => !submitted && setAnswers(a => ({ ...a, [qi]: letter }))}
                                    style={{ textAlign: 'left', padding: '10px 16px', borderRadius: 8, border: `2px solid ${isCorrect ? '#22c55e' : isWrong ? '#ef4444' : isSelected ? BLUE : '#e2e8f0'}`, background: isCorrect ? '#f0fdf4' : isWrong ? '#fef2f2' : isSelected ? '#eff6ff' : WHITE, cursor: submitted ? 'default' : 'pointer', fontSize: '0.88rem', color: '#1e293b', fontWeight: isSelected || isCorrect ? 600 : 400, transition: 'all 0.12s' }}>
                                    {isCorrect && <Check size={14} style={{ color: '#22c55e', marginRight: 6 }} />}
                                    {isWrong   && <XIcon size={14} style={{ color: '#ef4444', marginRight: 6 }} />}
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                    {submitted && q.explanation && (
                        <p style={{ marginTop: 12, padding: '10px 14px', background: '#f0f9ff', borderRadius: 8, fontSize: '0.83rem', color: '#0369a1', marginBottom: 0 }}>
                            <strong>Explanation:</strong> {q.explanation}
                        </p>
                    )}
                </div>
            ))}
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                {!submitted
                    ? <Button onClick={() => setSubmitted(true)} disabled={Object.keys(answers).length < questions.length} style={{ background: NAVY, border: 'none', borderRadius: 10, fontWeight: 700, padding: '10px 28px' }}>Submit Answers</Button>
                    : <Button onClick={generateMCQ} variant="outline-primary" className="rounded-pill" style={{ fontWeight: 600 }}><RotateCcw size={14} className="me-2" />New Quiz</Button>}
                <Button onClick={() => setMode('idle')} variant="light" className="rounded-pill">Back to Practice</Button>
            </div>
        </div>
    );

    if (mode === 'flashcard') {
        const card = flashcards[cardIdx];
        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '32px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, width: '100%', maxWidth: 560 }}>
                    <button onClick={() => setMode('idle')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}>
                        <ChevronLeft size={16} /> Back
                    </button>
                    <h5 style={{ margin: 0, fontWeight: 700, color: NAVY }}>Flashcards: {activeConcept.name}</h5>
                    <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '0.85rem' }}>{cardIdx + 1} / {flashcards.length}</span>
                </div>

                <div onClick={() => setFlipped(f => ({ ...f, [cardIdx]: !f[cardIdx] }))}
                    style={{ width: '100%', maxWidth: 560, height: 260, cursor: 'pointer', perspective: 1000, marginBottom: 24 }}>
                    <div style={{ position: 'relative', width: '100%', height: '100%', transformStyle: 'preserve-3d', transform: flipped[cardIdx] ? 'rotateY(180deg)' : 'rotateY(0deg)', transition: 'transform 0.45s ease' }}>
                        <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`, borderRadius: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, color: WHITE, boxShadow: '0 8px 32px rgba(30,58,110,0.25)' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7, marginBottom: 16 }}>Question</span>
                            <p style={{ textAlign: 'center', fontSize: '1.1rem', fontWeight: 700, margin: 0, lineHeight: 1.5 }}>{card?.front}</p>
                            <span style={{ marginTop: 20, opacity: 0.6, fontSize: '0.78rem' }}>Tap to reveal answer</span>
                        </div>
                        <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', background: WHITE, border: `2px solid ${BLUE}`, borderRadius: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, boxShadow: '0 8px 32px rgba(37,99,235,0.12)' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: BLUE, marginBottom: 16 }}>Answer</span>
                            <p style={{ textAlign: 'center', fontSize: '1rem', color: '#1e293b', lineHeight: 1.6, margin: 0 }}>{card?.back}</p>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <button onClick={() => { setCardIdx(i => Math.max(0, i - 1)); setFlipped(f => ({ ...f, [cardIdx]: false })); }} disabled={cardIdx === 0}
                        style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: cardIdx === 0 ? 'default' : 'pointer', opacity: cardIdx === 0 ? 0.4 : 1 }}>
                        ← Prev
                    </button>
                    <button onClick={() => { setCardIdx(i => Math.min(flashcards.length - 1, i + 1)); setFlipped(f => ({ ...f, [cardIdx]: false })); }} disabled={cardIdx === flashcards.length - 1}
                        style={{ background: BLUE, color: WHITE, border: 'none', borderRadius: 10, padding: '10px 20px', cursor: cardIdx === flashcards.length - 1 ? 'default' : 'pointer', fontWeight: 600, opacity: cardIdx === flashcards.length - 1 ? 0.4 : 1 }}>
                        Next →
                    </button>
                    <Button onClick={generateFlashcards} variant="outline-secondary" className="rounded-pill ms-2" size="sm"><RotateCcw size={13} className="me-1" />Shuffle</Button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <div style={{ background: `linear-gradient(120deg, ${NAVY}12, ${BLUE}18)`, borderRadius: 20, padding: '32px 40px', maxWidth: 480, width: '100%', textAlign: 'center' }}>
                <FlaskConical size={40} style={{ color: BLUE, marginBottom: 12 }} />
                <h5 style={{ fontWeight: 800, color: NAVY, marginBottom: 6 }}>Practice: {activeConcept.name}</h5>
                <p style={{ color: '#64748b', marginBottom: 28, lineHeight: 1.6 }}>Test your understanding with AI-generated quizzes or master terms with flip cards.</p>
                {err && <Alert variant="danger" className="small py-2 mb-3">{err}</Alert>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Button onClick={generateMCQ} style={{ background: NAVY, border: 'none', borderRadius: 12, padding: '14px', fontWeight: 700, fontSize: '0.95rem' }}>
                        <HelpCircle size={16} className="me-2" />Generate MCQ Quiz (5 questions)
                    </Button>
                    <Button onClick={generateFlashcards} style={{ background: `linear-gradient(90deg, ${BLUE}, #3b82f6)`, border: 'none', borderRadius: 12, padding: '14px', fontWeight: 700, fontSize: '0.95rem' }}>
                        <Lightbulb size={16} className="me-2" />Generate Flashcards (6 cards)
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  CROSSWORD WORKSPACE
// ─────────────────────────────────────────────────────────────────────────────

// Extract individual meaningful words from multi-word topic names.
// "Cardiac Cycle" → ["CARDIAC","CYCLE"] + "CARDIACCYCLE" (if short enough)
// This gives far more candidate words than treating the whole name as one word.
function extractCrosswordWords(syllabus) {
    const seen = new Set();
    const words = [];
    syllabus.forEach(unit => {
        unit.topics.forEach(topic => {
            const parts = topic.name.split(/[\s\-,/&()]+/).filter(Boolean);
            parts.forEach(part => {
                const w = part.toUpperCase().replace(/[^A-Z]/g, '');
                if (w.length >= 4 && w.length <= 14 && !seen.has(w)) {
                    seen.add(w);
                    words.push({ word: w, clue: `Part of "${topic.name}" (${unit.name})`, raw: topic.name, unit: unit.name });
                }
            });
            // Also try the whole name stripped of spaces
            const full = topic.name.toUpperCase().replace(/[^A-Z]/g, '');
            if (full.length >= 4 && full.length <= 14 && !seen.has(full)) {
                seen.add(full);
                words.push({ word: full, clue: `Topic in "${unit.name}"`, raw: topic.name, unit: unit.name });
            }
        });
    });
    return words;
}

const CW_DIFFICULTIES = [
    { key: 'easy',   label: 'Easy',   count: 6,  desc: '6 words'       },
    { key: 'medium', label: 'Medium', count: 10, desc: '10 words'      },
    { key: 'hard',   label: 'Hard',   count: 16, desc: 'Up to 16 words' },
];

const CrosswordWorkspace = ({ syllabus, activeProject }) => {
    const [cw,         setCw]         = useState(null);
    const [userGrid,   setUserGrid]   = useState([]);
    const [checked,    setChecked]    = useState(false);
    const [revealed,   setRevealed]   = useState(false);
    const [difficulty, setDifficulty] = useState('medium');
    const [wordPool,   setWordPool]   = useState([]);
    const [score,      setScore]      = useState(null); // {correct, total}
    const CELL = 34;

    // Build word pool whenever syllabus changes
    useEffect(() => {
        if (syllabus.length > 0) {
            setWordPool(extractCrosswordWords(syllabus));
        }
    }, [syllabus]);

    // Auto-generate when word pool or difficulty changes
    useEffect(() => {
        if (wordPool.length >= 2) generate(wordPool, difficulty);
    }, [wordPool, difficulty]); // eslint-disable-line

    const generate = (pool = wordPool, diff = difficulty) => {
        const cfg    = CW_DIFFICULTIES.find(d => d.key === diff) || CW_DIFFICULTIES[1];
        const count  = cfg.count;
        // Shuffle + slice to get a varied selection each time
        const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, count);
        if (shuffled.length < 2) return;
        const result = buildCrossword(shuffled);
        if (!result.placements.length) {
            // Try again with a different shuffle
            const retry = [...pool].sort(() => Math.random() - 0.5).slice(0, count);
            const r2 = buildCrossword(retry);
            if (r2.placements.length) {
                setCw(r2);
                setUserGrid(r2.grid.map(row => row.map(cell => (cell ? '' : null))));
            }
        } else {
            setCw(result);
            setUserGrid(result.grid.map(row => row.map(cell => (cell ? '' : null))));
        }
        setChecked(false); setRevealed(false); setScore(null);
    };

    if (!activeProject) return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
            <Award size={56} style={{ color: '#94a3b8', marginBottom: 16 }} />
            <h5 style={{ fontWeight: 700, color: '#334155' }}>Select a subject first</h5>
            <p style={{ color: '#64748b' }}>Pick a subject from the sidebar to generate a crossword from its topics.</p>
        </div>
    );

    if (!cw || !cw.placements.length) return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
            <Spinner animation="border" style={{ color: BLUE, width: 40, height: 40, borderWidth: 4, marginBottom: 16 }} />
            <p style={{ color: '#64748b', fontWeight: 600 }}>Building your crossword…</p>
        </div>
    );

    const handleCellInput = (r, c, val) => {
        const ch   = val.toUpperCase().replace(/[^A-Z]/g, '').slice(-1);
        const next = userGrid.map((row, ri) => row.map((cell, ci) => (ri === r && ci === c) ? ch : cell));
        setUserGrid(next);
    };

    const checkAnswers = () => {
        let correct = 0, total = 0;
        cw.grid.forEach((row, r) => row.forEach((cell, c) => {
            if (!cell) return;
            total++;
            if ((userGrid[r]?.[c] || '') === cell) correct++;
        }));
        setChecked(true);
        setScore({ correct, total });
    };

    const getCellStatus = (r, c) => {
        if (!checked && !revealed) return 'default';
        const answer = cw.grid[r][c];
        if (!answer) return 'default';
        if (revealed) return 'revealed';
        const user = userGrid[r]?.[c] || '';
        return user === answer ? 'correct' : user ? 'wrong' : 'empty';
    };

    const cellBg = (status) => {
        if (status === 'correct')  return '#dcfce7';
        if (status === 'wrong')    return '#fee2e2';
        if (status === 'revealed') return '#fef9c3';
        return WHITE;
    };

    const across = cw.placements.filter(p => p.dir === 'H').sort((a, b) => a.num - b.num);
    const down   = cw.placements.filter(p => p.dir === 'V').sort((a, b) => a.num - b.num);
    const isPerfect = score && score.correct === score.total;

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Toolbar */}
            <div style={{ padding: '10px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#f8faff' }}>
                <h6 style={{ fontWeight: 800, color: NAVY, margin: 0 }}>Concept Crossword</h6>
                <div style={{ display: 'flex', gap: 4, marginLeft: 4 }}>
                    {CW_DIFFICULTIES.map(d => (
                        <button key={d.key} onClick={() => setDifficulty(d.key)}
                            style={{ padding: '4px 12px', borderRadius: 20, border: `1px solid ${difficulty === d.key ? BLUE : '#e2e8f0'}`, background: difficulty === d.key ? BLUE : WHITE, color: difficulty === d.key ? WHITE : '#475569', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                            {d.label} <span style={{ opacity: 0.75 }}>({d.desc})</span>
                        </button>
                    ))}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    {score && (
                        <span style={{ padding: '4px 12px', borderRadius: 20, background: isPerfect ? '#dcfce7' : '#fef9c3', color: isPerfect ? '#15803d' : '#92400e', fontWeight: 700, fontSize: '0.8rem' }}>
                            {isPerfect ? '🎉 Perfect!' : `${score.correct}/${score.total} correct`}
                        </span>
                    )}
                    {!revealed && <Button size="sm" variant="outline-primary"   className="rounded-pill" onClick={checkAnswers}>Check</Button>}
                    <Button size="sm" variant="outline-warning"   className="rounded-pill" onClick={() => { setRevealed(true); setChecked(false); setScore(null); }}>Reveal</Button>
                    <Button size="sm" variant="outline-secondary" className="rounded-pill" onClick={() => generate()}><RotateCcw size={12} className="me-1" />New Puzzle</Button>
                </div>
            </div>

            {/* Main area */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {/* Grid */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                    <div style={{ border: `2px solid ${NAVY}`, borderRadius: 8, overflow: 'hidden', display: 'inline-block', boxShadow: '0 4px 16px rgba(30,58,110,0.12)' }}>
                        {cw.grid.map((row, r) => (
                            <div key={r} style={{ display: 'flex' }}>
                                {row.map((cell, c) => {
                                    const placement = cw.placements.find(p => p.row === r && p.col === c);
                                    const status    = getCellStatus(r, c);
                                    return (
                                        <div key={c} style={{ width: CELL, height: CELL, background: cell ? cellBg(status) : NAVY, border: cell ? `1px solid ${checked || revealed ? '#94a3b8' : '#cbd5e1'}` : 'none', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}>
                                            {placement && <span style={{ position: 'absolute', top: 2, left: 3, fontSize: 8, fontWeight: 700, color: NAVY, lineHeight: 1, userSelect: 'none' }}>{placement.num}</span>}
                                            {cell && (
                                                <input value={revealed ? cell : (userGrid[r]?.[c] || '')} onChange={e => handleCellInput(r, c, e.target.value)} readOnly={revealed} maxLength={1}
                                                    style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', textAlign: 'center', fontWeight: 800, fontSize: '0.95rem', color: revealed ? '#92400e' : status === 'correct' ? '#15803d' : status === 'wrong' ? RED : '#0f172a', cursor: revealed ? 'default' : 'text', outline: 'none', textTransform: 'uppercase', padding: 0 }} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Clue panel */}
                <div style={{ width: 260, borderLeft: '1px solid #e2e8f0', overflowY: 'auto', padding: 16, flexShrink: 0 }}>
                    <p style={{ fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: NAVY, marginBottom: 10 }}>
                        Across <span style={{ color: '#94a3b8', fontWeight: 400 }}>({across.length})</span>
                    </p>
                    {across.map(p => (
                        <div key={p.num} style={{ marginBottom: 10, padding: '6px 8px', borderRadius: 6, background: '#f8faff', borderLeft: `3px solid ${BLUE}` }}>
                            <span style={{ fontWeight: 700, color: BLUE, marginRight: 4, fontSize: '0.8rem' }}>{p.num}.</span>
                            <span style={{ color: '#334155', fontSize: '0.8rem' }}>{p.clue}</span>
                            <span style={{ color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginTop: 2 }}>{p.word.length} letters</span>
                        </div>
                    ))}

                    <p style={{ fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: NAVY, marginTop: 16, marginBottom: 10 }}>
                        Down <span style={{ color: '#94a3b8', fontWeight: 400 }}>({down.length})</span>
                    </p>
                    {down.map(p => (
                        <div key={p.num} style={{ marginBottom: 10, padding: '6px 8px', borderRadius: 6, background: '#f8faff', borderLeft: `3px solid ${RED}` }}>
                            <span style={{ fontWeight: 700, color: RED, marginRight: 4, fontSize: '0.8rem' }}>{p.num}.</span>
                            <span style={{ color: '#334155', fontSize: '0.8rem' }}>{p.clue}</span>
                            <span style={{ color: '#94a3b8', fontSize: '0.7rem', display: 'block', marginTop: 2 }}>{p.word.length} letters</span>
                        </div>
                    ))}

                    <div style={{ marginTop: 16, padding: '10px 12px', background: '#f0f6ff', borderRadius: 8, fontSize: '0.72rem', color: '#64748b' }}>
                        <strong style={{ color: NAVY }}>How to play:</strong>
                        <ul style={{ margin: '6px 0 0', paddingLeft: 14 }}>
                            <li>Click a cell and type a letter</li>
                            <li>Press Check to validate answers</li>
                            <li>Press New Puzzle for a fresh set</li>
                            <li>Change difficulty above for more words</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  PROGRESS DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
const DashboardWorkspace = ({ syllabus, mastery, activeProject }) => {
    if (!activeProject || !syllabus.length) return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
            <BarChart2 size={56} style={{ color: '#94a3b8', marginBottom: 16 }} />
            <h5 style={{ fontWeight: 700, color: '#334155' }}>Select a subject to view your progress</h5>
        </div>
    );

    const allTopics         = syllabus.flatMap(u => u.topics.map(t => ({ ...t, unit: u.name })));
    const visited           = allTopics.filter(t => (mastery[t.id] || 0) > 0);
    const coverage          = allTopics.length ? Math.round((visited.length / allTopics.length) * 100) : 0;
    const totalInteractions = Object.values(mastery).reduce((s, v) => s + v, 0);
    const topTopics         = allTopics.filter(t => mastery[t.id] > 0).sort((a, b) => (mastery[b.id] || 0) - (mastery[a.id] || 0)).slice(0, 10);
    const maxVisits         = topTopics.length ? Math.max(...topTopics.map(t => mastery[t.id] || 0)) : 1;
    const unvisited         = allTopics.filter(t => !(mastery[t.id] > 0)).slice(0, 5);
    const circleCirc        = 2 * Math.PI * 44;

    return (
        <div style={{ flex: 1, overflowY: 'auto', padding: 28 }}>
            <h5 style={{ fontWeight: 800, color: NAVY, marginBottom: 24 }}>Your Learning Progress — {activeProject.name}</h5>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
                <div style={{ background: WHITE, borderRadius: 16, padding: '24px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', textAlign: 'center', border: `1px solid ${BLUE}22` }}>
                    <svg width={100} height={100} viewBox="0 0 100 100" style={{ marginBottom: 8 }}>
                        <circle cx="50" cy="50" r="44" fill="none" stroke="#e2e8f0" strokeWidth="8" />
                        <circle cx="50" cy="50" r="44" fill="none" stroke={BLUE} strokeWidth="8"
                            strokeDasharray={circleCirc}
                            strokeDashoffset={circleCirc * (1 - coverage / 100)}
                            strokeLinecap="round"
                            style={{ transformOrigin: 'center', transform: 'rotate(-90deg)', transition: 'stroke-dashoffset 0.8s ease' }} />
                        <text x="50" y="50" textAnchor="middle" dominantBaseline="central" fontSize="18" fontWeight="800" fill={NAVY}>{coverage}%</text>
                    </svg>
                    <p style={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', margin: 0 }}>Course Coverage</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.75rem', margin: 0 }}>{visited.length} of {allTopics.length} topics</p>
                </div>

                <div style={{ background: WHITE, borderRadius: 16, padding: '24px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', textAlign: 'center', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ fontSize: '2.8rem', fontWeight: 800, color: BLUE, margin: 0, lineHeight: 1 }}>{totalInteractions}</p>
                    <p style={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', margin: '8px 0 0' }}>Total Interactions</p>
                    <p style={{ color: '#94a3b8', fontSize: '0.75rem', margin: 0 }}>Concept sessions</p>
                </div>

                <div style={{ background: WHITE, borderRadius: 16, padding: '24px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' }}>
                    <p style={{ fontWeight: 800, color: '#475569', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Recommended Next</p>
                    {unvisited.length > 0
                        ? unvisited.slice(0, 3).map(t => (
                            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: '0.82rem' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: RED, flexShrink: 0 }} />
                                <div>
                                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{t.name}</div>
                                    <div style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{t.unit}</div>
                                </div>
                            </div>
                        ))
                        : <p style={{ color: '#10b981', fontWeight: 700, fontSize: '0.9rem' }}>🎉 All topics visited!</p>}
                </div>
            </div>

            {topTopics.length > 0 && (
                <div style={{ background: WHITE, borderRadius: 16, padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0', marginBottom: 24 }}>
                    <p style={{ fontWeight: 800, color: NAVY, marginBottom: 20, fontSize: '0.92rem' }}>Most Explored Topics</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {topTopics.map(t => {
                            const visits = mastery[t.id] || 0;
                            const pct    = (visits / maxVisits) * 100;
                            const color  = visits >= 3 ? '#10b981' : BLUE;
                            return (
                                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 130, fontSize: '0.78rem', color: '#475569', fontWeight: 600, textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                                    <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 6, height: 20, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 6, transition: 'width 0.6s ease', minWidth: 4 }} />
                                    </div>
                                    <span style={{ width: 28, fontSize: '0.82rem', fontWeight: 700, color, textAlign: 'right', flexShrink: 0 }}>{visits}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', gap: 20, fontSize: '0.78rem', color: '#64748b' }}>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#94a3b8', marginRight: 4 }} />Not visited</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: BLUE, marginRight: 4 }} />Visited (1-2×)</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#10b981', marginRight: 4 }} />Practised (3+)</span>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  MINDMAP WORKSPACE — SVG bezier tree
// ─────────────────────────────────────────────────────────────────────────────
const MindmapWorkspace = ({ syllabus, mastery, activeProject, onSelectConcept }) => {
    const containerRef = useRef(null);
    const [dims, setDims] = useState({ w: 800, h: 500 });

    useEffect(() => {
        const obs = new ResizeObserver(entries => {
            const { width, height } = entries[0].contentRect;
            setDims({ w: width || 800, h: height || 500 });
        });
        if (containerRef.current) obs.observe(containerRef.current);
        return () => obs.disconnect();
    }, []);

    if (!activeProject || !syllabus.length) return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
            <GitBranch size={56} style={{ color: '#94a3b8', marginBottom: 16 }} />
            <h5 style={{ fontWeight: 700, color: '#334155' }}>Select a subject to see its learning map</h5>
        </div>
    );

    const { h }      = dims;
    const COL_ROOT   = 80 + 60;
    const COL_UNIT   = COL_ROOT + 200;
    const COL_TOPIC  = COL_UNIT  + 220;

    const allTopics   = syllabus.flatMap(u => u.topics);
    const totalTopics = allTopics.length;
    const svgH        = Math.max(h - 40, totalTopics * 42 + 80);

    const nodes = [];
    const links = [];
    nodes.push({ id: 'root', label: activeProject.name, x: COL_ROOT, y: svgH / 2, type: 'root' });

    let topicGlobalIdx = 0;
    syllabus.forEach((unit) => {
        const topicCount   = unit.topics.length;
        const topicStart   = topicGlobalIdx;
        const unitCentroid = (topicStart + topicCount / 2) / totalTopics;
        const unitY        = 40 + unitCentroid * (svgH - 80);
        const unitId       = `u-${unit.id}`;
        nodes.push({ id: unitId, label: unit.name, x: COL_UNIT, y: unitY, type: 'unit' });
        links.push({ from: 'root', to: unitId });
        unit.topics.forEach((topic) => {
            const topicY  = 40 + ((topicGlobalIdx + 0.5) / totalTopics) * (svgH - 80);
            const topicId = `t-${topic.id}`;
            const visits  = mastery[topic.id] || 0;
            nodes.push({ id: topicId, label: topic.name, x: COL_TOPIC, y: topicY, type: 'topic', visits, raw: topic });
            links.push({ from: unitId, to: topicId });
            topicGlobalIdx++;
        });
    });

    let recommendedId = null;
    for (const unit of syllabus) {
        for (const t of unit.topics) {
            if (!(mastery[t.id] > 0)) { recommendedId = `t-${t.id}`; break; }
        }
        if (recommendedId) break;
    }

    const nodeMap = {};
    nodes.forEach(n => (nodeMap[n.id] = n));

    const nodeColor = (n) => {
        if (n.type === 'root') return NAVY;
        if (n.type === 'unit') return BLUE;
        if (n.id === recommendedId) return RED;
        if (n.visits >= 3) return '#10b981';
        if (n.visits >= 1) return '#6366f1';
        return '#94a3b8';
    };
    const nodeW = (n) => n.type === 'root' ? 120 : n.type === 'unit' ? 150 : 160;
    const nodeH = (n) => n.type === 'topic' ? 28 : 36;

    return (
        <div ref={containerRef} style={{ flex: 1, overflow: 'auto', position: 'relative', background: '#f8faff' }}>
            <div style={{ position: 'absolute', top: 12, right: 16, display: 'flex', gap: 12, fontSize: '0.72rem', color: '#64748b', background: WHITE, padding: '8px 14px', borderRadius: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', zIndex: 2 }}>
                <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#94a3b8', marginRight: 4 }} />Not started</span>
                <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#6366f1', marginRight: 4 }} />Visited</span>
                <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#10b981', marginRight: 4 }} />Practised 3+</span>
                <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: RED,       marginRight: 4 }} />Recommended next</span>
            </div>

            <svg width={COL_TOPIC + 200} height={svgH} style={{ minWidth: '100%' }}>
                {links.map((lk, i) => {
                    const a = nodeMap[lk.from], b = nodeMap[lk.to];
                    if (!a || !b) return null;
                    const mx = (a.x + b.x) / 2;
                    return (
                        <path key={i}
                            d={`M ${a.x + nodeW(a) / 2} ${a.y} C ${mx} ${a.y} ${mx} ${b.y} ${b.x - nodeW(b) / 2} ${b.y}`}
                            fill="none" stroke={b.type === 'unit' ? `${BLUE}66` : '#94a3b844'} strokeWidth={b.type === 'unit' ? 2 : 1.5} />
                    );
                })}
                {nodes.map(n => {
                    const nw = nodeW(n), nh = nodeH(n);
                    const color         = nodeColor(n);
                    const isRecommended = n.id === recommendedId;
                    return (
                        <g key={n.id} style={{ cursor: n.type === 'topic' ? 'pointer' : 'default' }}
                            onClick={() => n.type === 'topic' && n.raw && onSelectConcept(n.raw)}>
                            <rect x={n.x - nw / 2} y={n.y - nh / 2} width={nw} height={nh} rx={nh / 2}
                                fill={n.type === 'topic' ? (n.visits > 0 ? color + '22' : '#f1f5f9') : color}
                                stroke={color} strokeWidth={isRecommended ? 2.5 : n.type !== 'topic' ? 0 : 1.5}
                                strokeDasharray={isRecommended ? '4 2' : 'none'} />
                            <text x={n.x} y={n.y} textAnchor="middle" dominantBaseline="central"
                                fontSize={n.type === 'root' ? 11 : n.type === 'unit' ? 10 : 9}
                                fontWeight={n.type !== 'topic' ? 700 : n.visits > 0 ? 600 : 400}
                                fill={n.type !== 'topic' ? WHITE : n.visits > 0 ? color : '#64748b'}>
                                {n.label.length > 18 ? n.label.slice(0, 17) + '…' : n.label}
                            </text>
                            {n.type === 'topic' && n.visits >= 3 && (
                                <text x={n.x + nw / 2 - 8} y={n.y - nh / 2 + 3} fontSize={8} fill="#10b981" fontWeight={700}>✓</text>
                            )}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  CORE CHAT COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const CreditBar = ({ label, used, limit, pct, resets_in, unlimited }) => {
    if (unlimited || limit == null) return null;
    const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
    return (
        <div className="mb-2">
            <div className="d-flex align-items-center justify-content-between mb-1">
                <span className="x-small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '0.05em' }}>{label}</span>
                <span className="x-small fw-bold" style={{ color }}>{Number(used).toFixed(2)} / {Number(limit).toFixed(2)}<span className="fw-normal text-muted ms-1">cr</span></span>
            </div>
            <div className="rounded-pill overflow-hidden" style={{ height: 5, background: '#e2e8f0' }}>
                <div className="rounded-pill h-100" style={{ width: `${Math.min(pct, 100)}%`, background: color, transition: 'width 0.4s ease' }} />
            </div>
            {pct < 100 && <p className="x-small text-muted mt-1 mb-0">Resets in {resets_in}</p>}
        </div>
    );
};

const CreditWidget = ({ credits }) => {
    const [expanded, setExpanded] = useState(false);
    if (!credits) return null;
    const allUnlimited = credits.daily?.unlimited && credits.weekly?.unlimited && credits.monthly?.unlimited;
    if (allUnlimited) return null;
    const maxPct  = Math.max(credits.daily?.pct || 0, credits.weekly?.pct || 0, credits.monthly?.pct || 0);
    const color   = maxPct >= 100 ? '#ef4444' : maxPct >= 80 ? '#f59e0b' : '#10b981';
    const label   = maxPct >= 100 ? 'Limit reached' : maxPct >= 80 ? `${Math.round(maxPct)}% used` : 'Credits OK';
    return (
        <div className="position-relative">
            <button onClick={() => setExpanded(e => !e)} className="d-flex align-items-center gap-1 border rounded-pill px-2 py-1"
                style={{ background: color + '20', borderColor: color + '44', color, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }} title="View credit usage">
                {maxPct >= 80 ? <AlertTriangle size={11} /> : <Zap size={11} />}
                <span className="d-none d-md-inline">{label}</span>
            </button>
            {expanded && (
                <div className="position-absolute end-0 top-100 mt-1 bg-white border rounded-3 shadow-lg p-3" style={{ width: 260, zIndex: 9999 }}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <p className="x-small fw-bold text-uppercase mb-0" style={{ color: 'var(--askos-accent, #6366f1)' }}><Zap size={11} className="me-1" />Credit Usage</p>
                        <button onClick={() => setExpanded(false)} className="border-0 bg-transparent p-0 text-muted" style={{ cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}>×</button>
                    </div>
                    <CreditBar label="Today" {...(credits.daily  || {})} />
                    <CreditBar label="Week"  {...(credits.weekly || {})} />
                    <CreditBar label="Month" {...(credits.monthly|| {})} />
                    {maxPct >= 100 && credits.message && <Alert variant="danger"  className="py-2 x-small mb-0 mt-2" role="alert">{credits.message}</Alert>}
                    {maxPct >= 80  && maxPct < 100    && <Alert variant="warning" className="py-2 x-small mb-0 mt-2" role="alert">⚠️ Approaching credit limit.</Alert>}
                </div>
            )}
        </div>
    );
};

const ReferenceFooter = ({ references }) => {
    if (!references || !references.length) return null;
    return (
        <div className="mt-2 pt-2" style={{ borderTop: '1px dashed #dee2e6' }}>
            <p className="x-small text-muted fw-bold text-uppercase mb-1"><BookMarked size={11} className="me-1" />Source</p>
            {references.map((ref, i) => (
                <Badge key={i} className="badge-soft-primary fw-normal me-1 mb-1" style={{ fontSize: '0.68rem' }} title={ref.paragraphs?.join(', ')}>
                    📖 {ref.book}{ref.pages ? ` · ${ref.pages}` : ''}
                </Badge>
            ))}
        </div>
    );
};

const MessageBubble = ({ msg, onFollowUp, onConfused }) => {
    const isUser     = msg.role === 'user';
    const rawContent = isUser ? msg.content : stripFollowUps(msg.content);
    const followUps  = isUser ? [] : extractFollowUps(msg.content);
    const refs       = isUser ? [] : (msg.references || []);
    const content    = isUser ? rawContent : normalizeMathForMarkdown(rawContent);
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(toPlainText(stripFollowUps(msg.content))).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    }, [msg.content]);

    return (
        <div className={`d-flex mb-4 ${isUser ? 'justify-content-end' : 'justify-content-start'}`}>
            {!isUser && (
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0, marginTop: 2 }}>
                    <GraduationCap size={16} style={{ color: WHITE }} />
                </div>
            )}
            <div style={{ maxWidth: '80%' }}>
                <div className={isUser ? 'chat-bubble-user' : 'chat-bubble-assistant'}>
                    {isUser && msg.imagePreview && (
                        <img src={msg.imagePreview} alt="Attached" style={{ width: '100%', maxWidth: 260, borderRadius: 8, marginBottom: 8, display: 'block' }} />
                    )}
                    {isUser ? (
                        <p className="mb-0 small">{content}</p>
                    ) : (
                        <>
                            <div className="markdown-body small">
                                <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}
                                    components={{ p: ({ node, ...props }) => <p className="mb-2" style={{ wordBreak: 'break-word' }} {...props} />, ul: ({ node, ...props }) => <ul className="mb-2 ps-3" {...props} />, ol: ({ node, ...props }) => <ol className="mb-2 ps-3" {...props} /> }}>
                                    {content}
                                </ReactMarkdown>
                            </div>
                            <ReferenceFooter references={refs} />
                            <div className="d-flex align-items-center gap-2 mt-2 pt-1" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                <button onClick={handleCopy} className="d-flex align-items-center gap-1 border-0 bg-transparent x-small text-muted p-0" style={{ cursor: 'pointer' }}>
                                    {copied ? <><Check size={12} className="text-success" /><span className="text-success">Copied</span></> : <><Copy size={12} /><span>Copy</span></>}
                                </button>
                                <span className="text-muted" style={{ fontSize: '0.55rem' }}>·</span>
                                <button onClick={() => onConfused(msg)} className="d-flex align-items-center gap-1 border-0 bg-transparent x-small text-muted p-0" style={{ cursor: 'pointer' }}>
                                    <HelpCircle size={12} /><span>Still confused?</span>
                                </button>
                            </div>
                        </>
                    )}
                </div>
                {followUps.length > 0 && (
                    <div className="mt-2">
                        <p className="x-small text-muted fw-bold text-uppercase mb-1"><Lightbulb size={11} className="me-1" />Explore further</p>
                        <div className="d-flex flex-wrap gap-2">
                            {followUps.map((q, i) => (
                                <Button key={i} variant="outline-primary" size="sm" className="rounded-pill fw-normal" style={{ fontSize: '0.72rem' }} onClick={() => onFollowUp(q)}>{q}</Button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const TypingIndicator = () => (
    <div className="d-flex mb-3 align-items-center">
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0 }}>
            <GraduationCap size={16} style={{ color: WHITE }} />
        </div>
        <div className="chat-bubble-assistant py-2 px-3">
            <div className="d-flex gap-1 align-items-center" style={{ height: 18 }}>
                {[0, 1, 2].map(i => (
                    <div key={i} className="rounded-circle bg-secondary" style={{ width: 7, height: 7, opacity: 0.5, animation: 'bounce 1.2s infinite', animationDelay: `${i * 0.2}s` }} />
                ))}
            </div>
        </div>
    </div>
);

const FloatingReader = ({ language, speed, voiceType, voiceName }) => {
    const [sel, setSel] = useState({ text: '', x: 0, y: 0, show: false });
    useEffect(() => {
        const onUp = (e) => {
            if (e.target.closest('#read-aloud-popup')) return;
            const s = window.getSelection(); const text = s?.toString().trim();
            if (text && text.length > 1) {
                const range = s.getRangeAt(0); const rect = range.getBoundingClientRect();
                setSel({ text, x: rect.left + rect.width / 2, y: rect.top - 45, show: true });
            } else { setSel(p => ({ ...p, show: false })); }
        };
        const onDown = (e) => { if (!e.target.closest('#read-aloud-popup')) setSel(p => ({ ...p, show: false })); };
        document.addEventListener('mouseup', onUp); document.addEventListener('mousedown', onDown);
        return () => { document.removeEventListener('mouseup', onUp); document.removeEventListener('mousedown', onDown); };
    }, []);
    if (!sel.show) return null;
    return (
        <div id="read-aloud-popup"
            style={{ position: 'fixed', top: sel.y, left: sel.x, transform: 'translateX(-50%)', zIndex: 9999, backgroundColor: 'var(--askos-accent, #6366f1)', color: WHITE, padding: '6px 12px', borderRadius: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold' }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); speakText(sel.text, language, Number(speed) || 1.0, voiceType, voiceName || null); setSel(p => ({ ...p, show: false })); window.getSelection()?.removeAllRanges(); }}>
            <Volume2 size={14} /> Read Aloud
        </div>
    );
};

const MicButton = ({ onTranscript, isListening, setIsListening, recognitionRef, language = 'en' }) => {
    const toggle = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { showToast('Speech recognition requires Chrome or Edge.', 'info'); return; }
        if (isListening) { recognitionRef.current?.stop(); return; }
        recognitionRef.current = new SR();
        recognitionRef.current.continuous = false; recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = LANG_MAP[language] || 'en-IN';
        recognitionRef.current.onresult = e => onTranscript(e.results[0][0].transcript);
        recognitionRef.current.onerror  = () => setIsListening(false);
        recognitionRef.current.onend    = () => setIsListening(false);
        recognitionRef.current.start(); setIsListening(true);
    };
    return (
        <Button variant={isListening ? 'danger' : 'outline-secondary'} className="rounded-circle d-flex align-items-center justify-content-center p-0" style={{ width: 38, height: 38, flexShrink: 0 }} onClick={toggle} title={isListening ? 'Stop listening (Ctrl+M)' : 'Speak (Ctrl+M)'}>
            {isListening ? <Mic size={16} className="animate-pulse" /> : <MicOff size={16} />}
        </Button>
    );
};

const ImageAttachButton = ({ onImage, disabled }) => {
    const fileRef = useRef(null);
    const handleFile = (e) => {
        const file = e.target.files[0]; if (!file) return;
        if (!file.type.startsWith('image/')) { showToast('Please select an image file.', 'warning'); return; }
        if (file.size > 10 * 1024 * 1024) { showToast('Image must be smaller than 10 MB.', 'warning'); return; }
        const reader = new FileReader();
        reader.onload = (ev) => onImage({ dataUrl: ev.target.result, mimeType: file.type, fileName: file.name });
        reader.readAsDataURL(file);
        e.target.value = '';
    };
    return (
        <>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/heic" style={{ display: 'none' }} onChange={handleFile} />
            <Button variant="outline-secondary" className="rounded-circle d-flex align-items-center justify-content-center p-0" style={{ width: 38, height: 38, flexShrink: 0 }} onClick={() => fileRef.current?.click()} disabled={disabled} title="Attach image">
                <Paperclip size={16} />
            </Button>
        </>
    );
};

const ImagePreview = ({ image, onRemove }) => {
    if (!image) return null;
    return (
        <div className="d-flex align-items-center gap-2 px-1 pb-2">
            <div className="position-relative" style={{ flexShrink: 0 }}>
                <img src={image.dataUrl} alt="Attached" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '2px solid var(--askos-accent, #6366f1)' }} />
                <button onClick={onRemove} style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#ef4444', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                    <XIcon size={11} color="#fff" />
                </button>
            </div>
            <div className="min-w-0">
                <p className="x-small fw-semibold mb-0 text-truncate" style={{ maxWidth: 160 }}>{image.fileName}</p>
                <p className="x-small text-muted mb-0"><ImageIcon size={10} className="me-1" />Image attached</p>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  STUDENT AVATAR
// ─────────────────────────────────────────────────────────────────────────────
const StudentAvatar = ({ profile, photo, size = 36, onClick }) => {
    const initials = (profile?.full_name || profile?.email || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    return (
        <button onClick={onClick} title={profile?.full_name || profile?.email || 'Profile'}
            style={{ width: size, height: size, borderRadius: '50%', background: photo ? 'transparent' : BLUE, border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, cursor: onClick ? 'pointer' : 'default', padding: 0 }}>
            {photo
                ? <img src={photo} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ color: WHITE, fontSize: size * 0.37, fontWeight: 700, lineHeight: 1 }}>{initials}</span>}
        </button>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
const AskOSApp = ({ authSession, onLogout }) => {
    const { slug } = authSession;

    const [profile,       setProfile]       = useState(null);
    const [projects,      setProjects]      = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    const [syllabus,      setSyllabus]      = useState([]);
    const [activeConcept, setActiveConcept] = useState(null);
    const [chatSession,   setChatSession]   = useState(null);
    const [messages,      setMessages]      = useState([]);
    const [input,         setInput]         = useState('');
    const [loading,       setLoading]       = useState(false);
    const [error,         setError]         = useState('');

    const [isListening,   setIsListening]   = useState(false);
    const recognitionRef                    = useRef(null);

    const [showSettings,  setShowSettings]  = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const [prefs,         setPrefs]         = useState(loadPrefs);
    const [practiceMode,  setPracticeMode]  = useState(false);
    const [mastery,       setMastery]       = useState({});
    const [stagedImage,   setStagedImage]   = useState(null);
    const [credits,       setCredits]       = useState(null);
    const [expandedUnits, setExpandedUnits] = useState({});
    const [branding,      setBranding]      = useState(null);
    const [activeTab,     setActiveTab]     = useState('chat');
    const [conceptImage,  setConceptImage]  = useState(null);
    const [localPhoto,    setLocalPhoto]    = useState(() => localStorage.getItem(photoKey(slug)) || '');

    const bottomRef          = useRef(null);
    const inputRef           = useRef(null);
    const creditsIntervalRef = useRef(null);
    const dispatch           = useDispatch();
    const navigate           = useNavigate();

    useEffect(() => { applyPreferences(prefs); }, []); // eslint-disable-line
    useEffect(() => { if (!prefs.voiceEnabled) stopSpeaking(); }, [prefs.voiceEnabled]);
    useEffect(() => { return () => { recognitionRef.current?.stop(); stopSpeaking(); }; }, []);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

    useEffect(() => {
        const fetch = async () => { try { const r = await api.get(`/tenant/${slug}/askos/me/credits`, { headers: askosAuthHeader() }); setCredits(r.data); } catch {} };
        fetch(); creditsIntervalRef.current = setInterval(fetch, 5 * 60 * 1000);
        return () => clearInterval(creditsIntervalRef.current);
    }, [slug]);

    useEffect(() => {
        const init = async () => {
            try {
                // Fetch branding and dispatch to Redux so navbar + other components stay consistent
                api.get(`/public/tenant-branding/${slug}`).then(r => {
                    const b = { name: r.data.name || '', logo: r.data.logo || '' };
                    setBranding(b);
                    dispatch(setReduxBranding(b));
                }).catch(() => {});

                const [profRes, projRes] = await Promise.all([
                    api.get(`/tenant/${slug}/askos/me`,       { headers: askosAuthHeader() }),
                    api.get(`/tenant/${slug}/askos/projects`, { headers: askosAuthHeader() }),
                ]);
                setProfile(profRes.data);
                setProjects(projRes.data);

                // Load profile photo: prefer backend (cross-device) over localStorage cache
                if (profRes.data?.profile_photo) {
                    setLocalPhoto(profRes.data.profile_photo);
                    localStorage.setItem(photoKey(slug), profRes.data.profile_photo);
                } else {
                    const cached = localStorage.getItem(photoKey(slug));
                    if (cached) setLocalPhoto(cached);
                }

                if (profRes.data?.language_preference) {
                    const saved = loadPrefs();
                    if (saved.language === 'en' && profRes.data.language_preference !== 'en') {
                        const merged = { ...saved, language: profRes.data.language_preference };
                        setPrefs(merged); savePrefs(merged); applyPreferences(merged);
                    }
                }
                if (projRes.data.length === 1) selectProject(projRes.data[0]);
            } catch {
                localStorage.removeItem('askos_token'); localStorage.removeItem('askos_slug');
                navigate('/askos');
            }
        };
        init();
    }, [slug]); // eslint-disable-line

    const studentLanguage = prefs.language || profile?.language_preference || 'en';

    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape') { stopSpeaking(); setStagedImage(null); return; }
            if (!e.ctrlKey && !e.metaKey) return;
            switch (e.key.toLowerCase()) {
                case 'm':
                    e.preventDefault();
                    inputRef.current?.blur();
                    if (isListening) recognitionRef.current?.stop();
                    else {
                        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
                        if (SR) {
                            recognitionRef.current = new SR();
                            recognitionRef.current.continuous = false; recognitionRef.current.interimResults = false;
                            recognitionRef.current.lang = LANG_MAP[studentLanguage] || 'en-IN';
                            recognitionRef.current.onresult = ev => handleTranscript(ev.results[0][0].transcript);
                            recognitionRef.current.onerror = () => setIsListening(false);
                            recognitionRef.current.onend   = () => setIsListening(false);
                            recognitionRef.current.start(); setIsListening(true);
                        }
                    }
                    break;
                case 'r':
                    e.preventDefault();
                    if (prefs.voiceEnabled) {
                        const lastAI = [...messages].reverse().find(m => m.role === 'assistant');
                        if (lastAI) speakText(stripFollowUps(lastAI.content), studentLanguage, Number(prefs.voiceSpeed) || 1.0, prefs.voiceType || 'female', prefs.voiceName || null);
                    }
                    break;
                case 'b': e.preventDefault(); bookmarkLastResponse(); break;
                case ',': e.preventDefault(); setShowSettings(true); break;
                default: break;
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isListening, messages, prefs, studentLanguage]); // eslint-disable-line

    const bookmarkLastResponse = useCallback(() => {
        const lastAI = [...messages].reverse().find(m => m.role === 'assistant');
        if (!lastAI) return;
        const key = `askos_bookmarks_${slug}`;
        const bm  = { id: Date.now(), concept: activeConcept?.name || 'General', content: toPlainText(stripFollowUps(lastAI.content)), savedAt: new Date().toISOString() };
        localStorage.setItem(key, JSON.stringify([bm, ...JSON.parse(localStorage.getItem(key) || '[]')].slice(0, 50)));
        const orig = document.title; document.title = '✓ Bookmarked!'; setTimeout(() => { document.title = orig; }, 1500);
    }, [messages, slug, activeConcept]);

    const fetchConceptImage = async (concept) => {
        if (!activeProject) return;
        setConceptImage(null);
        try {
            const r = await api.get(`/tenant/${slug}/askos/unsplash`, { headers: askosAuthHeader(), params: { query: `${concept.name} education` } });
            if (r.data.results?.length) setConceptImage(r.data.results[0]);
        } catch { /* silent — Unsplash is optional */ }
    };

    const selectProject = async (project) => {
        setActiveProject(project); setActiveConcept(null); setChatSession(null);
        setMessages([]); setError(''); stopSpeaking(); setStagedImage(null);
        setConceptImage(null); setMastery(loadMastery(slug, project.id));
        try {
            const r = await api.get(`/tenant/${slug}/askos/projects/${project.id}/syllabus`, { headers: askosAuthHeader() });
            setSyllabus(r.data);
            setExpandedUnits(r.data.length > 0 ? { [r.data[0].id]: true } : {});
        } catch { setSyllabus([]); setExpandedUnits({}); }
    };

    const initSession = async (projectId, conceptId = null) => {
        try {
            const r = await api.post(`/tenant/${slug}/askos/session/init`, { project_id: projectId, concept_id: conceptId }, { headers: askosAuthHeader() });
            const session   = r.data;
            const restored  = restoreMessages(session.session_id);
            const msgs      = restored || session.history || [];
            setChatSession(session); setMessages(msgs);
            return { ...session, _restoredMessages: msgs };
        } catch { setError('Could not start session. Please try again.'); return null; }
    };

    const selectConcept = async (concept) => {
        setActiveConcept(concept); stopSpeaking(); setConceptImage(null);
        if (!activeProject) return;
        const sess = await initSession(activeProject.id, concept.id);
        if (sess) setTimeout(() => inputRef.current?.focus(), 100);
        if (activeProject) { const updated = markVisited(slug, activeProject.id, concept.id); setMastery(updated); }
        fetchConceptImage(concept);
        setActiveTab('chat');
    };

    const PRACTICE_SUFFIX = practiceMode
        ? '\n\n[PRACTICE MODE ACTIVE] Do NOT give the answer directly. Ask one Socratic question to guide the student toward the answer.'
        : '';

    const sendMessage = async (text, imageOverride = null) => {
        const msg = (text || input).trim();
        if (!msg || loading) return;
        setInput(''); setError(''); stopSpeaking();
        const imageToSend = imageOverride || stagedImage;
        setStagedImage(null);
        let currentSession = chatSession;
        if (!currentSession && activeProject) currentSession = await initSession(activeProject.id, activeConcept?.id);
        if (!currentSession) return;

        const userBubble  = { role: 'user', content: msg, references: [], imagePreview: imageToSend?.dataUrl || null };
        const nextMessages = [...messages, userBubble];
        setMessages(nextMessages); setLoading(true);

        try {
            const payload = { session_id: currentSession.session_id, message: msg + PRACTICE_SUFFIX, concept_id: activeConcept?.id || null, language: studentLanguage };
            if (imageToSend) { payload.image_base64 = imageToSend.dataUrl.split(',')[1]; payload.image_mime = imageToSend.mimeType; }
            const r = await api.post(`/tenant/${slug}/askos/chat`, payload, { headers: askosAuthHeader() });

            if (r.data.concept && !activeConcept) {
                const resolved = { id: r.data.concept_id, name: r.data.concept };
                setActiveConcept(resolved);
                if (activeProject) { const updated = markVisited(slug, activeProject.id, r.data.concept_id); setMastery(updated); }
            }

            const history    = r.data.history || [];
            const references = r.data.references || [];
            const annotated  = history.map((m, idx) => (m.role === 'assistant' && idx === history.length - 1) ? { ...m, references } : m);
            const withImages = annotated.map((m, idx) => (m.role === 'user' && idx === annotated.length - 2 && imageToSend) ? { ...m, imagePreview: imageToSend.dataUrl } : m);
            setMessages(withImages);
            persistMessages(currentSession.session_id, withImages);

            if (prefs.voiceEnabled && document.activeElement !== inputRef.current) {
                const lastAI = withImages[withImages.length - 1];
                if (lastAI?.role === 'assistant') speakText(stripFollowUps(lastAI.content), studentLanguage, Number(prefs.voiceSpeed) || 1.0, prefs.voiceType || 'female', prefs.voiceName || null);
            }
        } catch (e) {
            const detail  = e.response?.data?.detail || e.message;
            const isQuota = e.response?.status === 402 || String(detail).includes('credit limit');
            const errMsg  = [{ role: 'assistant', content: isQuota ? detail : `I encountered an error: ${detail}. Please try again.`, references: [], isQuotaBlock: isQuota }];
            const withErr = [...nextMessages, ...errMsg];
            setMessages(withErr); persistMessages(currentSession.session_id, withErr);
            if (isQuota) { try { const r = await api.get(`/tenant/${slug}/askos/me/credits`, { headers: askosAuthHeader() }); setCredits(r.data); } catch {} }
        } finally { setLoading(false); setTimeout(() => inputRef.current?.focus(), 50); }
    };

    const handleConfused  = useCallback(() => {
        sendMessage("Can you explain that differently with a simpler example? I'm still confused.");
    }, [sendMessage]); // eslint-disable-line

    const handleTranscript = (t) => { setInput(t); setTimeout(() => sendMessage(t), 150); };

    const handleLogout = () => {
        recognitionRef.current?.stop(); stopSpeaking();
        // Remove only AskOS auth tokens — do NOT call dispatch(logout()) as that
        // runs localStorage.clear() which would wipe the cached profile photo.
        localStorage.removeItem('askos_token');
        localStorage.removeItem('askos_slug');
        onLogout();
        navigate('/askos');
    };

    const handlePrefsChange = (newPrefs) => {
        setPrefs(newPrefs); savePrefs(newPrefs); applyPreferences(newPrefs);
        if (newPrefs.language && newPrefs.language !== prefs.language) {
            api.patch(`/tenant/${slug}/askos/me`, { language_preference: newPrefs.language }, { headers: askosAuthHeader() }).catch(() => {});
        }
    };

    const lastConceptKey  = `askos_last_concept_${slug}_${activeProject?.id}`;
    const saveLastConcept = (concept) => { try { localStorage.setItem(lastConceptKey, JSON.stringify({ id: concept.id, name: concept.name })); } catch {} };
    const loadLastConcept = () => { try { const r = localStorage.getItem(lastConceptKey); return r ? JSON.parse(r) : null; } catch { return null; } };
    useEffect(() => { if (activeConcept) saveLastConcept(activeConcept); }, [activeConcept]); // eslint-disable-line
    const lastConcept = activeProject && messages.length === 0 ? loadLastConcept() : null;

    // ── Sidebar ──────────────────────────────────────────────────────────────
    const Sidebar = (
        <aside style={{ width: 260, flexShrink: 0, overflowY: 'auto', background: WHITE, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #f1f5f9' }}>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 8 }}>Subjects</p>
                {projects.length === 0 && <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>No subjects available</p>}
                {projects.map(p => (
                    <button key={p.id} onClick={() => selectProject(p)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', borderRadius: 8, padding: '8px 10px', marginBottom: 4, fontSize: '0.83rem', fontWeight: activeProject?.id === p.id ? 700 : 400, background: activeProject?.id === p.id ? `${BLUE}15` : 'transparent', color: activeProject?.id === p.id ? BLUE : '#334155', cursor: 'pointer', transition: 'all 0.12s' }}>
                        <BookOpen size={14} style={{ flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    </button>
                ))}
            </div>

            {syllabus.length > 0 && (
                <div style={{ padding: '12px', flex: 1 }}>
                    <p style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 8 }}>Topics</p>
                    {syllabus.map(unit => {
                        const isExpanded = expandedUnits[unit.id];
                        const hasTopics = (unit.topics || []).length > 0;
                        // A concept with no child topics is still a real,
                        // selectable concept_id — the chat/content pipeline
                        // keys off concept_id, not topic_id, so let the
                        // concept row itself be clicked directly instead of
                        // only expanding into an empty list.
                        const unitActive = !hasTopics && activeConcept?.id === unit.id;
                        return (
                            <div key={unit.id} style={{ marginBottom: 4 }}>
                                <button onClick={() => hasTopics ? setExpandedUnits(p => ({ ...p, [unit.id]: !isExpanded })) : selectConcept(unit)}
                                    title={hasTopics ? undefined : 'No sub-topics — click to chat about this concept directly'}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', border: 'none', borderRadius: 6, background: unitActive ? `${BLUE}15` : 'transparent', padding: '4px 6px', cursor: 'pointer', marginBottom: 2 }}>
                                    <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: unitActive ? BLUE : '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{unit.name}</span>
                                    {hasTopics
                                        ? (isExpanded ? <ChevronDown size={13} style={{ color: '#94a3b8', flexShrink: 0 }} /> : <ChevronRight size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />)
                                        : <ChevronRight size={11} style={{ color: unitActive ? BLUE : 'transparent', flexShrink: 0 }} />}
                                </button>
                                {isExpanded && hasTopics && (
                                    <div style={{ marginLeft: 8, paddingLeft: 8, borderLeft: '2px solid #e2e8f0', marginBottom: 8 }}>
                                        {unit.topics.map(t => {
                                            const visits   = mastery[t.id] || 0;
                                            const isActive = activeConcept?.id === t.id;
                                            return (
                                                <button key={t.id} onClick={() => selectConcept(t)}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', border: 'none', borderRadius: 6, padding: '5px 8px', marginBottom: 2, fontSize: '0.78rem', background: isActive ? `${BLUE}15` : 'transparent', color: isActive ? BLUE : '#475569', fontWeight: isActive ? 700 : 400, cursor: 'pointer', transition: 'all 0.1s' }}>
                                                    {isActive ? <ChevronRight size={11} style={{ color: BLUE, flexShrink: 0 }} /> : <span style={{ width: 11, flexShrink: 0 }} />}
                                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                                                    {visits > 0 && (
                                                        <span title={`Visited ${visits}×`} style={{ fontSize: '0.6rem', fontWeight: 700, flexShrink: 0, color: visits >= 3 ? '#10b981' : '#6366f1' }}>
                                                            {visits >= 3 ? '✓✓' : '✓'}
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {Object.keys(mastery).length > 0 && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9', fontSize: '0.68rem', color: '#94a3b8' }}>
                            <span style={{ color: '#6366f1', fontWeight: 700 }}>✓</span> Visited &nbsp;·&nbsp;
                            <span style={{ color: '#10b981', fontWeight: 700 }}>✓✓</span> Practised (3+)
                        </div>
                    )}
                </div>
            )}
        </aside>
    );

    // ── Chat workspace ───────────────────────────────────────────────────────
    const ChatWorkspace = (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px', overflow: 'hidden' }}>
            <div style={{ flex: 1, background: WHITE, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 8px' }}>
                    {messages.length === 0 && (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 300 }}>
                            {lastConcept && lastConcept.id !== activeConcept?.id ? (
                                <div style={{ background: `linear-gradient(135deg, ${NAVY}08, ${BLUE}12)`, border: `1px solid ${BLUE}30`, borderRadius: 16, padding: '20px 28px', maxWidth: 400, marginBottom: 24, width: '100%' }}>
                                    <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: BLUE, marginBottom: 6 }}>Welcome back 👋</p>
                                    <p style={{ fontWeight: 700, color: NAVY, marginBottom: 8 }}>Continue with <em>{lastConcept.name}</em>?</p>
                                    <Button size="sm" onClick={() => { const t = syllabus.flatMap(u => u.topics).find(t => t.id === lastConcept.id); if (t) selectConcept(t); }}
                                        style={{ background: BLUE, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem' }}>
                                        Pick up where you left off →
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${NAVY}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                                        <GraduationCap size={30} style={{ color: NAVY }} />
                                    </div>
                                    <h5 style={{ fontWeight: 800, color: NAVY, marginBottom: 8 }}>
                                        {practiceMode ? 'Practice Mode Active' : activeConcept ? `Chatting: ${activeConcept.name}` : `Hi${profile ? `, ${(profile.full_name || '').split(' ')[0]}` : ''}! I'm AskOS`}
                                    </h5>
                                    <p style={{ color: '#64748b', maxWidth: 340, lineHeight: 1.6 }}>
                                        {practiceMode
                                            ? "I'll guide you with questions instead of answers. Select a topic and ask away!"
                                            : activeProject
                                                ? `Pick a topic from the sidebar or ask me anything about ${activeProject.name}.`
                                                : 'Select a subject from the sidebar to get started.'}
                                    </p>
                                </>
                            )}

                            {conceptImage && (
                                <div style={{ width: '100%', maxWidth: 380, marginTop: 8, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                                    <img src={conceptImage.url_regular} alt={conceptImage.alt_text || activeConcept?.name}
                                        style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                                    <div style={{ background: '#f8fafc', padding: '6px 12px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                        Photo by <a href={conceptImage.author_link} target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>{conceptImage.author_name}</a> · Unsplash
                                    </div>
                                </div>
                            )}

                            {activeProject && syllabus.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 420, marginTop: 20 }}>
                                    {syllabus.slice(0, 2).flatMap(u => u.topics.slice(0, 2)).map(t => (
                                        <Button key={t.id} variant="outline-primary" size="sm" className="rounded-pill fw-normal" style={{ fontSize: '0.75rem' }} onClick={() => selectConcept(t)}>{t.name}</Button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {messages.map((msg, i) => (
                        <MessageBubble key={i} msg={msg} onFollowUp={q => sendMessage(q)} onConfused={handleConfused} />
                    ))}
                    {loading && <TypingIndicator />}
                    {error && (
                        <Alert variant="danger" className="small py-2 d-flex align-items-center gap-2" role="alert">
                            {error}
                            <Button variant="link" size="sm" className="p-0 ms-auto text-danger" onClick={() => setError('')}><RefreshCw size={14} /></Button>
                        </Alert>
                    )}
                    <div ref={bottomRef} />
                </div>

                <div className="chat-input-area">
                    <ImagePreview image={stagedImage} onRemove={() => setStagedImage(null)} />
                    {prefs.voiceEnabled && (
                        <p className="x-small text-center mb-1" style={{ color: 'var(--askos-accent, var(--classroom-primary))' }}>
                            <Volume2 size={11} className="me-1" />Voice responses enabled · {LANG_MAP[studentLanguage] || 'en-IN'}
                            {practiceMode && <span className="ms-2">· 🧪 Practice Mode</span>}
                        </p>
                    )}
                    {!prefs.voiceEnabled && practiceMode && (
                        <p className="x-small text-center mb-1" style={{ color: '#d97706' }}>🧪 Practice Mode — I'll guide you with questions</p>
                    )}
                    <p className="x-small text-muted text-center mb-2">AskOS answers only from your verified course content · Not a substitute for your professor</p>
                    <div className="d-flex align-items-end gap-2">
                        <MicButton isListening={isListening} setIsListening={setIsListening} recognitionRef={recognitionRef} language={studentLanguage} onTranscript={handleTranscript} />
                        <ImageAttachButton disabled={!activeProject || loading} onImage={img => setStagedImage(img)} />
                        <Form.Control ref={inputRef} as="textarea" rows={1} value={input} onChange={e => setInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                            disabled={!activeProject || loading}
                            placeholder={isListening ? 'Listening…' : stagedImage ? 'Describe what you need help with…' : activeConcept ? `Ask anything about ${activeConcept.name}…` : 'Ask your professor anything…'}
                            className={`shadow-none ${isListening ? 'border-primary' : ''}`}
                            style={{ resize: 'none', minHeight: 42, maxHeight: 120, borderRadius: 12 }} />
                        <Button variant="primary" className="d-flex align-items-center justify-content-center p-0 rounded-circle" style={{ width: 42, height: 42, flexShrink: 0, background: BLUE, border: 'none' }}
                            onClick={() => sendMessage()} disabled={(!input.trim() && !stagedImage) || !activeProject || loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : <Send size={16} />}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="classroom-body" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#f0f6ff' }}>
            <FloatingReader language={studentLanguage} speed={prefs.voiceSpeed || 1.0} voiceType={prefs.voiceType || 'female'} voiceName={prefs.voiceName || null} />

            {/* NAV — sticky, never scrolls away */}
            <nav style={{ background: NAVY, height: 56, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', boxShadow: '0 2px 16px rgba(0,0,0,0.3)', position: 'sticky', top: 0, zIndex: 100 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 160 }}>
                    <img src={branding?.logo || '/assets/images/logo.png'} alt="Logo" height="28" style={{ objectFit: 'contain', maxWidth: 120 }} onError={e => { e.target.src = '/assets/images/logo.png'; }} />
                    <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', fontWeight: 600 }}>{branding?.name || 'AskOS'}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {TABS.map(({ id, label, Icon }) => {
                        const active = activeTab === id;
                        return (
                            <button key={id} onClick={() => setActiveTab(id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: active ? 700 : 500, background: active ? 'rgba(255,255,255,0.16)' : 'transparent', color: active ? WHITE : 'rgba(255,255,255,0.6)', transition: 'all 0.15s', backdropFilter: active ? 'blur(4px)' : 'none' }}>
                                <Icon size={14} />
                                <span className="d-none d-lg-inline">{label}</span>
                            </button>
                        );
                    })}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 160, justifyContent: 'flex-end' }}>
                    <button onClick={() => setPracticeMode(p => !p)} title={practiceMode ? 'Socratic Mode ON' : 'Enable Socratic Mode'}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, background: practiceMode ? '#f59e0b22' : 'rgba(255,255,255,0.1)', color: practiceMode ? '#fbbf24' : 'rgba(255,255,255,0.6)' }}>
                        <FlaskConical size={13} />
                        <span className="d-none d-md-inline">{practiceMode ? 'Guided' : 'Learn'}</span>
                    </button>

                    <button onClick={() => handlePrefsChange({ ...prefs, voiceEnabled: !prefs.voiceEnabled })} title={prefs.voiceEnabled ? 'Voice ON' : 'Enable voice responses'}
                        style={{ background: prefs.voiceEnabled ? `${BLUE}44` : 'transparent', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer', color: prefs.voiceEnabled ? '#93c5fd' : 'rgba(255,255,255,0.6)' }}>
                        {prefs.voiceEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                    </button>

                    <CreditWidget credits={credits} />

                    <button onClick={() => setShowShortcuts(true)} title="Keyboard shortcuts"
                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 6, borderRadius: 6 }}>
                        <Keyboard size={16} />
                    </button>

                    <button onClick={() => setShowSettings(true)} title="Settings (Ctrl+,)"
                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 6, borderRadius: 6 }}>
                        <Settings size={16} />
                    </button>

                    <StudentAvatar profile={profile} photo={localPhoto} size={34} onClick={() => setShowSettings(true)} />

                    <button onClick={handleLogout} title="Sign out"
                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 6, borderRadius: 6 }}>
                        <LogOut size={15} />
                    </button>
                </div>
            </nav>

            {practiceMode && activeTab === 'chat' && (
                <div style={{ background: '#fffbeb', borderBottom: '1px solid #fef08a', padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexShrink: 0 }}>
                    <FlaskConical size={13} style={{ color: '#d97706' }} />
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#92400e' }}>Socratic Mode — AskOS will guide you with questions instead of giving direct answers.</span>
                    <button onClick={() => setPracticeMode(false)} style={{ background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: '0.78rem', marginLeft: 8 }}>✕ Exit</button>
                </div>
            )}

            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {Sidebar}

                <main style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    {activeTab === 'chat' && ChatWorkspace}
                    {activeTab === 'practice' && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 16 }}>
                            <div style={{ flex: 1, background: WHITE, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <PracticeWorkspace activeProject={activeProject} activeConcept={activeConcept} slug={slug} />
                            </div>
                        </div>
                    )}
                    {activeTab === 'crossword' && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 16 }}>
                            <div style={{ flex: 1, background: WHITE, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <CrosswordWorkspace syllabus={syllabus} activeProject={activeProject} />
                            </div>
                        </div>
                    )}
                    {activeTab === 'dashboard' && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 16 }}>
                            <div style={{ flex: 1, background: WHITE, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <DashboardWorkspace syllabus={syllabus} mastery={mastery} activeProject={activeProject} />
                            </div>
                        </div>
                    )}
                    {activeTab === 'mindmap' && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 16 }}>
                            <div style={{ flex: 1, background: WHITE, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                <MindmapWorkspace syllabus={syllabus} mastery={mastery} activeProject={activeProject} onSelectConcept={selectConcept} />
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {/* ── Footer ── */}
            <footer style={{ height: 36, background: NAVY, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, flexShrink: 0, zIndex: 50 }}>
                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.68rem', marginRight: 14 }}>
                    © {new Date().getFullYear()} AcademicOS · Powered by TatvaOne AI
                </span>
                {[
                    ['Privacy Policy',    '/privacy'],
                    ['Terms of Use',      '/terms'],
                    ['AI Ethics Policy',  '/ai-ethics'],
                    ['Data Processing',   '/dpa'],
                    ['Cookie Policy',     '/cookies'],
                ].map(([label, href], i, arr) => (
                    <React.Fragment key={href}>
                        <a href={href} target="_blank" rel="noreferrer"
                            style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', textDecoration: 'none', whiteSpace: 'nowrap', transition: 'color 0.15s' }}
                            onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.9)'}
                            onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.5)'}>
                            {label}
                        </a>
                        {i < arr.length - 1 && <span style={{ color: 'rgba(255,255,255,0.2)', margin: '0 8px', fontSize: '0.65rem' }}>|</span>}
                    </React.Fragment>
                ))}
            </footer>

            <SettingsPanel
                show={showSettings}
                onHide={() => setShowSettings(false)}
                profile={profile}
                slug={slug}
                prefs={prefs}
                onPrefsChange={handlePrefsChange}
                onPhotoChange={(photo) => setLocalPhoto(photo)}
            />
            <ShortcutsModal show={showShortcuts} onHide={() => setShowShortcuts(false)} />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ROOT — auth gate
// ─────────────────────────────────────────────────────────────────────────────
const AskOS = () => {
    const storedToken = localStorage.getItem('askos_token');
    const storedSlug  = localStorage.getItem('askos_slug');
    const [authSession, setAuthSession] = useState(
        storedToken && storedSlug ? { slug: storedSlug, access_token: storedToken } : null
    );
    if (!authSession) return <AskOSLoginPage onLogin={sess => setAuthSession(sess)} />;
    return <AskOSApp authSession={authSession} onLogout={() => setAuthSession(null)} />;
};

export default AskOS;
